/**
 * Order builder — the cart write. TRANSPORT ONLY.
 *
 * What to send is decided by rf-order-payload.js, which is pure and tested under
 * node. What things cost is decided by rf-pricing.js, likewise. This file makes
 * the requests, checks what came back, and either redirects or refuses. Keeping
 * the split means the dangerous decisions are provable without a browser and only
 * the plumbing needs one.
 *
 * THE SEQUENCE, and every branch of it:
 *
 *   guard        per-form flag set SYNCHRONOUSLY, before the first await
 *   refuse       the two preconditions, both BEFORE any cart call: past the
 *                self-serve ceiling, and nothing in the order at all (R2). Each
 *                gets its own message and returns; neither records a failure,
 *                because nothing failed -- the order was never placeable.
 *   refuse #3    the Guarantee (R4). buildCartPayload() throws
 *                `guarantee_unavailable: ` when the $0.00 variant cannot be
 *                resolved, and it is called at the TOP of run(), synchronously,
 *                above the first await -- so no cart call has happened either.
 *                Unlike the two above this one IS a failure: it is recorded and
 *                logged, because a store that cannot resolve its own catalogue
 *                has gone wrong, and it gets its own copy so a merchant can see
 *                which setting did it.
 *   clear.js     fails -> ABORT. Proceeding would bill the previous
 *                configuration plus the new one.
 *   add.js       items + attributes + note + per-line properties, ONE atomic
 *                call. Fails -> nothing was added (verified: a batch with one
 *                bad id adds nothing), so the cart is empty and the customer's
 *                configuration is still on screen. Recoverable.
 *   cart.js      read back and assert. Fails -> zero OUR OWN lines before
 *                surfacing, or a chargeable cart at a total we just called wrong
 *                stays reachable via /checkout by URL, by history, and by
 *                Shopify's own abandoned-checkout email.
 *   redirect     only after the assert passes.
 *
 * A 200 IS NOT SUCCESS. Shopify returns Cart Errors as HTTP 200 with a `status`
 * field in the body, and add.js answers an unknown variant with 422 rather than
 * the 404 its own docs show for update.js. Both are handled by reading the body.
 *
 * IT NEVER TOUCHES A DISCOUNT CODE (R4). Not to apply one, and -- deliberately --
 * not to clear one either. A code on the cart may be the customer's own, applied
 * moments earlier from a /discount/ link, and deleting it just before payment
 * would be hostile. The assert reads `original_total_price`, which is the
 * PRE-discount figure, so a customer's code cannot trip it.
 *
 * AND SINCE 3 SEP 2026 IT HAS A SECOND, MUCH SMALLER JOB (R6, U11): on /cart it
 * puts back a $0.00 Guarantee line that has been removed. Same file, because the
 * four things that job needs are private to this one -- the cross-tab locks, the
 * transport below, the module-level listener registrations, and the CommonJS
 * hatch. Read guaranteeLineAction() for the decision, reAddGuaranteeLine() for
 * the one-at-a-time guard and writeGuaranteeLine() for the writes. It shares
 * NOTHING with the sequence above except the plumbing: it never clears, never
 * writes a cart-wide key, and touches no line but the Guarantee.
 *
 * THAT JOB IS A WRITE ON THE LAST SCREEN BEFORE PAYMENT, so two of its three
 * hazards are about who else is writing at the same time and one is about the
 * customer leaving mid-write. All three are handled where they arise and none of
 * them is a retry: writeGuaranteeLine() re-reads the submit lock AFTER its add
 * settles and unwinds the add if the window closed under it; it holds a second,
 * short-TTL lock of its own so two /cart tabs cannot both add; and the delegated
 * `submit` listener at the foot of this file makes the Checkout button wait up
 * to CHECKOUT_WAIT_MS -- never the 15-second fetch deadline -- for a pending
 * write rather than navigating past it.
 *
 * Requires rf-pricing.js, rf-order-payload.js and rf-order-builder.js first;
 * layout/theme.liquid pins that order.
 */
(function () {
  'use strict';

  // Dual-purpose the same way rf-pricing.js and rf-order-payload.js are: a theme
  // asset in the browser, a CommonJS module under `node --test`. assertCart(),
  // cartError() and allowedIds() are pure -- plain data in, a string or null out
  // -- and they are the last thing between a wrong cart and checkout, so a test
  // has to be able to reach them. No build step.
  var isCommonJS = typeof module === 'object' && module.exports;
  var pricing = isCommonJS ? require('./rf-pricing.js') : window.PhotokivePricing;
  // REQUIRED UNDER NODE TOO, since 3 Sep 2026. It was `null` here, on the reading
  // that only the boot path needs it and the boot path is skipped -- true until
  // minimumRefusal() below started asking rf-order-payload.js whether an order is
  // sellable. That question is the whole of step 9b, and with `null` it could only
  // ever be answered in a browser. Requiring it changes nothing in the browser
  // branch and adds no cycle: rf-order-payload.js requires rf-pricing.js, and
  // neither requires this file.
  var payloadMod = isCommonJS ? require('./rf-order-payload.js')
                              : window.PhotokiveOrderPayload;

  /** Where a failed submit is remembered until the next successful one (D18). */
  var FAILURE_KEY = 'rf-ob-failures';
  /** Cap the remembered reasons; the attribute has an undocumented size limit. */
  var FAILURE_REASON_CAP = 5;

  /* ── sessionStorage, every access wrapped ─────────────────────────────────
     Safari private browsing throws on write, and a cleared or mangled store is
     normal rather than exceptional. Losing the diagnostic is acceptable; failing
     the submit over it is not. */

  function readFailures() {
    try {
      var raw = window.sessionStorage.getItem(FAILURE_KEY);
      if (!raw) return null;
      var rec = JSON.parse(raw);
      return rec && rec.count ? rec : null;
    } catch (e) {
      return null;
    }
  }

  function recordFailure(reason) {
    try {
      var rec = readFailures() || { count: 0, reasons: [] };
      rec.count = Number(rec.count) + 1;
      rec.reasons = (rec.reasons || []).concat([String(reason)]).slice(-FAILURE_REASON_CAP);
      window.sessionStorage.setItem(FAILURE_KEY, JSON.stringify(rec));
    } catch (e) {
      /* no diagnostic is better than no order */
    }
  }

  function clearFailures() {
    try {
      window.sessionStorage.removeItem(FAILURE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  /* ── transport ────────────────────────────────────────────────────────── */

  function route(path) {
    // routes has no checkout_url, so /checkout is composed. Everything else goes
    // through Shopify.routes so a locale prefix is honoured -- irrelevant while
    // the store is US-only, and exactly the kind of thing that breaks silently
    // the day Canada is re-enabled.
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base.replace(/\/$/, '') + path;
  }

  /**
   * Every cart call gets a deadline. Without one a stalled connection leaves the
   * submit button disabled forever with no error and no way back -- the promise
   * simply never settles, so the `finally` that re-enables the form never runs.
   * Added 23 Aug 2026; before that there was no timeout on any of the three calls.
   *
   * AbortController rather than AbortSignal.timeout, which Safari only shipped in
   * 16. Where AbortController is missing too, behaviour is exactly as before.
   */
  var FETCH_TIMEOUT_MS = 15000;

  function deadline() {
    if (typeof AbortController === 'undefined') {
      return { signal: undefined, clear: function () {} };
    }
    var c = new AbortController();
    var t = setTimeout(function () { c.abort(); }, FETCH_TIMEOUT_MS);
    return { signal: c.signal, clear: function () { clearTimeout(t); } };
  }

  async function postJSON(path, body) {
    var d = deadline();
    try {
      var res = await fetch(route(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : '{}',
        signal: d.signal
      });
      var data = null;
      try {
        data = await res.json();
      } catch (e) {
        data = null;
      }
      return { res: res, data: data };
    } finally {
      d.clear();
    }
  }

  /**
   * Guarded the same way postJSON is. It used to be a bare `return res.json()`,
   * so a 5xx HTML body or a proxy interstitial rejected here -- and because the
   * caller did not catch it, the cleanup that removes the lines we just added was
   * skipped entirely. Returns the parsed cart, or throws with something readable.
   */
  async function getCart() {
    var d = deadline();
    try {
      var res = await fetch(route('/cart.js'), {
        headers: { Accept: 'application/json' },
        // NO-STORE. Every caller reads this to decide whether the cart matches what
        // was asked for, and a stale body produces a false mismatch, an unnecessary
        // unwind and a stranded order. That direction fails SAFE, so this is a
        // quality improvement and not a guard -- nothing depends on it, and no test
        // observes it (the fakes record url, body and method, never `cache`).
        cache: 'no-store',
        signal: d.signal
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      try {
        return await res.json();
      } catch (e) {
        throw new Error('the cart response was not JSON');
      }
    } finally {
      d.clear();
    }
  }

  /**
   * Undo our own add, and CONFIRM it. update.js takes absolute quantities, so
   * zeroing by id is idempotent and leaves anything we did not add alone.
   *
   * This used to be fire-and-forget inside the mismatch branch: its result was
   * discarded, and postJSON does not throw on a 429, a 5xx, or Shopify's
   * 200-with-`status` Cart Error -- the very shape cartError() exists to detect.
   * A silent failure there left a chargeable cart live behind a message saying
   * nothing had been charged. Returns null on success, or a reason.
   *
   * ── IT REFUSES TO UNWIND A CART SOMEONE ELSE WROTE, AND THE TEST FOR THAT IS
   *    NOW THE LOCK, NOT A QUANTITY COMPARISON ────────────────────────────────
   *
   * The quantity comparison below is kept and is still worth having, but it was
   * the WHOLE guard until 3 Sep 2026 and it is structurally blind to two of the
   * four lines this payload can carry. It drops an id when the cart's quantity
   * differs from the one this payload asked for -- and the Guarantee is in every
   * payload at quantity 1, always, so for that line the condition is
   * unsatisfiable. Same for the USB, which is quantity 1 or absent. The loser of
   * a cross-tab race therefore deleted a line out of the winner's asserted cart
   * every time, which is the exact failure the guard was written to prevent.
   *
   * lockTakenOver() is not blind to it, because it does not look at the cart at
   * all: run() takes the submit lock with an owner token, and if the key now
   * names a DIFFERENT owner then another writer has taken the cart since. That
   * also arms the guard on the path where the quantity comparison cannot run --
   * a getCart() throw hands this function no cart, and a read failure is exactly
   * the condition most likely to coincide with contention.
   *
   * AN UNREADABLE OR ABSENT LOCK MEANS PROCEED, deliberately. takeLock() swallows
   * a storage failure ("a lock we cannot store is a lock we do not hold"), and on
   * Safari in private mode that is every submit; refusing the unwind there would
   * trade a cleanup for a live chargeable cart, which is the worse of the two.
   *
   * AND THE QUANTITY COMPARISON IS NOW ALL-OR-NOTHING. It used to drop the
   * offending id and zero the rest, so a race on one line still deleted the
   * others. If any of our ids is present at a quantity we did not ask for, the
   * cart is not ours to unwind and the whole cleanup is refused and reported.
   */
  async function removeOurLines(payload, cart) {
    if (lockTakenOver(LOCK_KEY)) return 'cleanup_skipped_foreign_writer';

    var zeros = {};
    // SINCE 3 SEP 2026 THIS ZEROES THE GUARANTEE LINE TOO, because payload.items
    // now carries it. That is correct -- the Guarantee was added by this submit
    // and belongs to the same unwind -- but it is worth naming, because it is the
    // one line the theme puts back by itself. A customer who lands on /cart after
    // a STRANDED submit meets a cart this function has just partially unwound, and
    // U11's re-add runs on that page. TWO things now keep the two out of each
    // other's way, and the second is the one that survives a released lock: U11
    // holds off while the submit lock is HELD, and every exit from this submit
    // releases it -- so the durable half is the attribute cleared at the write
    // below, which shuts U11's gate on the cart this unwind leaves behind.
    //
    // AND 0 IS THE ONLY NUMBER THAT MAY EVER APPEAR ON THE RIGHT OF THIS LINE.
    // `updates` is an UPSERT, not modify-only -- measured 11 Sep 2026 through
    // `shopify theme dev` on :9292, Shopify CLI 4.8.0, and recorded in
    // docs/cart-contract.sh R10 Q2b: "the SAME absent id at a NON-ZERO quantity
    // ADDS a line ... so only the ZERO case is a safe no-op". Every id here comes
    // from a payload that may never have reached the cart, so a quantity read from
    // anywhere at all would ADD the chargeable lines this function exists to take
    // out. The literal is the guard.
    payload.items.forEach(function (i) { zeros[i.id] = 0; });

    // DO NOT zero ANYTHING when a line of ours sits at a quantity we did not ask
    // for. The original comment claimed this "leaves anything we did not add
    // alone", which was true of other variants and false of the same variant at a
    // different quantity -- so a second tab's cleanup could delete the line this
    // tab had already asserted and was checking out with. A differing quantity
    // means another writer touched the cart, and the safe move is to leave the
    // whole thing and report; see this function's header for why that is now
    // all-or-nothing, and for the two lines it can never see.
    if (cart && Array.isArray(cart.items)) {
      // SUMMED PER VARIANT, for the reason at the top of assertCart(): a partial
      // line-item discount splits one of our lines into several, and compared line
      // by line every split read as a foreign quantity and refused the unwind.
      var held = {};
      var linesOf = {};
      cart.items.forEach(function (line) {
        var k = String(line.variant_id);
        held[k] = (held[k] || 0) + line.quantity;
        (linesOf[k] = linesOf[k] || []).push(line);
      });
      var foreign = false;
      payload.items.forEach(function (i) {
        var k = String(i.id);
        if (Object.prototype.hasOwnProperty.call(held, k) && held[k] !== i.quantity) foreign = true;
      });
      if (foreign) return 'cleanup_skipped_foreign_quantity';

      // A SPLIT VARIANT IS ZEROED BY LINE KEY. A bare variant id zeroes ONE line of
      // that variant (R10 Q4 below), so on a split cart the unwind left half the
      // order behind and reported cleanup_incomplete. Measured on the dev store
      // 2 Oct 2026: zeroing by variant id left one photo-set line; zeroing every
      // line by `key` emptied the cart, and a body mixing line keys with variant ids
      // was accepted and applied. A variant with ONE line keeps its variant-id
      // zero, so an unsplit cart sends exactly the write it always has.
      payload.items.forEach(function (i) {
        var lines = linesOf[String(i.id)] || [];
        var keyed = lines.length > 1 && lines.every(function (l) { return l.key; });
        if (!keyed) return;
        delete zeros[i.id];
        lines.forEach(function (l) { zeros[l.key] = 0; });
      });
    }
    try {
      touchLock(LOCK_KEY);
      // ── AND THE SAME POST CLOSES THE THEME'S OTHER CART WRITER (R6) ────────
      //
      // THE HAZARD. /cart/clear.js does NOT clear cart attributes -- measured,
      // docs/cart-contract.sh section 7: "an attribute SURVIVES clear.js" -- and
      // the add above sends `attributes: payload.attributes`. So after a PARTIAL
      // unwind the cart can hold a subset of the chargeable lines AND every
      // cart-wide key the add wrote. The /cart page's re-add gate is
      // `item_count > 0` AND the version key matching the rendered config, and
      // that cart satisfies both. A customer landing on /cart after a STRANDED
      // submit would meet a theme adding a fresh $0.00 Guarantee onto a cart this
      // function has just declared stranded -- a cart that then LOOKS repaired,
      // is reachable through /checkout and Shopify's abandoned-checkout email,
      // and carries `_lines_digest`, `_order_value_list_cents` and the deposit
      // basis describing an order that was never placed. Kive reconciles against
      // that digest and finds lines that no longer exist.
      //
      // ONE KEY, NOT ALL OF THEM. Clearing the version key alone shuts the
      // gate and stops the digest being read as current. The rest are left
      // deliberately: they are the evidence of what this submit asked for, they
      // may belong to another tab, and this file is conservative about rewriting
      // cart-wide keys on purpose.
      //
      // THE MECHANISM IS `''`, NOT OMISSION. MEASURED 11 Sep 2026, Shopify CLI
      // 4.8.0, docs/cart-contract.sh R10 Q6: `{updates, attributes}` in ONE POST
      // is accepted and BOTH halves apply, neither ignored -- and attributes
      // MERGE rather than replace, so a key left out stays exactly as it was.
      // Shopify normalises an attribute written as '' to DELETED, which is the
      // same mechanism rf-order-payload.js already uses for the balance key.
      //
      // AND IT CANNOT HALF-APPLY. R10 Q3: the batch is ALL-OR-NOTHING. If the
      // zeros are refused, this clear is refused with them and the function
      // returns `cleanup_rejected:` -- which is the right pairing, because a cart
      // that kept every chargeable line also kept attributes that still DESCRIBE
      // those lines truthfully. There is no arrangement in which the lines go and
      // the key stays, or the key goes and the lines stay.
      //
      // WHICH OUTCOMES THIS RIDES ON IS DECIDED BY POSITION, NOT BY A BRANCH.
      // The two REFUSALS above -- the lock guard and the quantity comparison --
      // return before this statement, so a cart that may belong to another tab
      // keeps its keys untouched; clearing them there would sabotage the re-add
      // of a tab that is mid-checkout with a correctly asserted cart. Everything
      // below this line WROTE, so everything below it is cleared: the takeover
      // found after the write, an incomplete confirm, a throw in the confirm,
      // and the CLEAN result too.
      //
      // ON THE CLEAN BRANCH THIS IS A FREE RIDER, AND THE ARGUMENT THAT USED TO
      // STAND HERE WAS BACKWARDS. It read: "a cart that is empty now does not
      // stay empty: on the transport class this whole unit exists for, the
      // origin may apply the add AFTER the zeros were confirmed clean, at which
      // point `item_count` climbs back above zero with the version key still
      // current. That is precisely the cart the gate must not open on." The late
      // add RE-WRITES THE VERSION KEY OVER THIS CLEAR: the add carries the whole
      // attributes object rf-order-payload.js built, version key included, and it
      // lands after the last write this client will ever make. Both legs of the
      // gate are satisfied again, by a write this client cannot undo, so on that
      // one path the clear achieves nothing at all. Driven rather than argued --
      // "U5: the ordering this does NOT close" in tests/order-builder-cart.test.js
      // asserts the key is back on the cart after the clear.
      //
      // WHERE IT IS LOAD-BEARING, which is why it stays: `cleanup_incomplete`,
      // where lines survive the zeros and `item_count` never drops; a takeover
      // found after the write, where we cannot say what the zeros landed on; and
      // the refused paths below, where the batch is turned away wholesale and the
      // cart keeps every chargeable line. On each of those the gate would open on
      // a cart this function has declared stranded.
      //
      // AND IT IS UNCONDITIONAL BECAUSE THAT IS THE CHEAP SHAPE, not because the
      // clean branch needs it. The outcome is not known until this POST has been
      // answered, so a "clear only when unclean" rule would need a SECOND write on
      // the money path -- which is the shape this file refuses everywhere else --
      // and the clear costs nothing riding a write that is going out regardless.
      var out = await postJSON('/cart/update.js', {
        updates: zeros,
        attributes: { _pricing_version: '' }
      });
      var err = cartError(out);
      if (err) return 'cleanup_rejected: ' + err;

      // THE CHECK THAT IS ACTUALLY ATOMIC WITH THE WRITE. Same idiom as the
      // re-add's, and here for the same reason: the guard at the top of this
      // function was evaluated before a write that is allowed 15 seconds, and a
      // writer arriving inside that window is invisible to it. Everything below
      // would then be a claim about SOMEONE ELSE'S cart -- the confirming read
      // says "our ids are gone", which is true of their cart and tells this
      // customer nothing about their own order.
      //
      // ITS OWN REASON, NOT THE ONE ABOVE. `cleanup_skipped_foreign_writer`
      // means the zeroing never went out; this one means it did, and we cannot
      // say what it landed on. Both are strand-class -- the customer is told the
      // cart may still hold the order -- but an operator reading the log needs
      // to know whether a write was issued.
      if (lockTakenOver(LOCK_KEY)) return 'cleanup_taken_over_after_write';

      // Confirm rather than assume, because this is the claim the customer-facing
      // copy rests on -- BUT CONFIRM FROM THE ANSWER WE ALREADY HAVE.
      //
      // MEASURED 11 Sep 2026, docs/cart-contract.sh R10 Q7: "the update.js response
      // body IS a cart -- it carries an items array", and "it is the POST-update
      // cart, identical to the following /cart.js -- a caller can skip that second
      // read". Skipping it is worth two things. It takes a call ALLOWED fifteen
      // seconds off a path where the customer may already have waited fifteen for
      // the add to fail, with the form frozen; and it removes a failure mode of its
      // own, because a cart this function had just cleaned whose CONFIRMING READ
      // then dropped was reported to that customer as stranded.
      //
      // THE FALLBACK STAYS. A body that is not a cart -- a proxy interstitial, a
      // truncated response -- leaves the question unanswered, and the answer to an
      // unanswered question here is not "clean".
      var after = out.data;
      if (!after || !Array.isArray(after.items)) {
        touchLock(LOCK_KEY);
        after = await getCart();
      }
      // AGAINST THE PAYLOAD'S IDS, NOT `zeros`: a split variant is keyed there by
      // line key, so asking `zeros` would miss a half the write failed to remove.
      var left = (after.items || []).filter(function (l) {
        return payload.items.some(function (i) { return String(i.id) === String(l.variant_id); });
      });
      // R10 Q4, same measurement: zeroing a bare variant id removes ONE LINE of that
      // variant, not every line of it. Two lines of one variant happen by design --
      // add.js merges on variant + selling plan + PROPERTIES, and a partial
      // line-item discount splits one line in two -- so since 2 Oct 2026 any of our
      // variants holding more than one keyed line is zeroed BY LINE KEY above, and
      // every line goes in the one write. A line left over now means the write did
      // not remove it, or one of the two cases that still fall back to a bare id:
      // no cart to read keys from (getCart() threw), or a duplicate line without a
      // `key`.
      //
      // THERE IS DELIBERATELY NO SECOND PASS. A write loop on the money path whose
      // terminating condition is a cart read is the shape this repo has rejected
      // three times. This fails SAFE -- the customer is told the cart may still
      // hold the order, which it does.
      if (left.length) return 'cleanup_incomplete';
      return null;
    } catch (e) {
      return 'cleanup_threw';
    }
  }

  /**
   * ⚠ THE STATUS CODE IS NOT A STABLE SIGNAL IN EITHER DIRECTION, so the BODY is
   * read first and `res.ok` only as a fallback. This comment said "A Cart Error
   * arrives as HTTP 200 with a `status` field, so `res.ok` is true for a failure" --
   * measured 10 Sep 2026, both a nonexistent variant and an unapplicable selling
   * plan returned HTTP 422 carrying that same body. Neither value can be pinned.
   * Shopify writes `description` for shoppers, so it is passed through rather than
   * replaced with something vaguer.
   */
  function cartError(out) {
    if (out.data && out.data.status) {
      return shopperText(out) || out.data.message || 'Cart error';
    }
    if (!out.res.ok) {
      return shopperText(out) || (out.data && out.data.message) ||
             ('HTTP ' + out.res.status);
    }
    return null;
  }

  /**
   * SHOPIFY'S SHOPPER-FACING WORDING, and the empty string where it sent none.
   *
   * `description` AND NOTHING ELSE, which is the whole point of the split from
   * cartError() above. That function has to return SOMETHING, because a caller
   * needs a reason to log, so it falls back through `message` to a status line it
   * synthesises itself. Neither fallback is words for a shopper: `message` on a
   * Cart Error body is the literal string "Cart Error", and `HTTP 503` is a
   * transport code. A customer shown either has been told nothing, so run() asks
   * this narrower question before it decides to pass a reason through, and lands on
   * the section's own line when the answer is empty.
   *
   * NOT A TEST ON THE TEXT ITSELF, EVER. Shopify LOCALISES `description`; matching
   * an English pattern against it is how a German storefront once matched nothing
   * and refused every order. The only question asked here is whether the field is
   * populated.
   */
  function shopperText(out) {
    return (out && out.data && out.data.description) || '';
  }

  /* ── the /cart re-add, executed (R6) ──────────────────────────────────────
     The DECISION is guaranteeLineAction(), a pure function beside assertCart();
     everything here is the plumbing that carries it out. Split for the reason
     this file's header gives for the whole split: the dangerous half is provable
     under node, and only the plumbing needs a browser.

     ── THE HARD CONSTRAINT: THIS PATH WRITES ONLY THE GUARANTEE LINE ──────────

     Two endpoints, both aimed at one variant: /cart/add.js with a single item at
     the guarantee id, and /cart/change.js at a line key of that same variant. It
     sends NO `attributes`, NO `note`, and touches no other line -- not on the
     happy path and not on ANY error path.

     THE ONE REPAIR IT PERFORMS IS OF ITS OWN WRITE, and it is the exception that
     proves the rule rather than a hole in it. When the submit lock turns out to
     have been taken while our add was in flight, one /cart/change.js at the key
     the add response returned subtracts exactly what we asked for and then stops.
     Nothing else is repaired, on any branch: every other failure below is a log
     and a stop, because every repair worth attempting there would mean writing
     something other than this one line.

     THAT IS NOT TIDINESS, IT IS THE WHOLE SAFETY ARGUMENT. The one customer who
     reaches this code with a hand-crafted cart is, by definition, the one who has
     been crafting POSTs. If a stumble here rewrote the cart attributes to agree
     with whatever lines they had left behind, the theme would be performing the
     second half of their attack: re-signing a tampered cart with a fresh
     `_lines_digest` and a matching `_order_value_list_cents`, which is exactly the
     evidence Kive reconciles against. Leaving the attributes alone means a
     tampered cart stays visibly tampered.

     Pinned from both sides in tests/order-builder-cart.test.js: statically, that
     this function's body names no other endpoint and no `attributes` key; and by
     driving it, that a cart full of foreign lines comes back with every foreign
     line and every attribute byte-identical. */

  /**
   * THE PENDING WRITE ITSELF, or null -- not a boolean.
   *
   * It is two things at once and both are load-bearing. It is the one-at-a-time
   * guard boot() and a bfcache restore need, because those can both fire within
   * a tick. And it is the handle the delegated `submit` listener at the foot of
   * this file awaits, which a boolean could not be: the Checkout button is a
   * plain form POST, live from first paint, and a customer who presses it while
   * these two round trips are in flight would otherwise navigate away and place
   * the order with no Guarantee line on it -- silently, with nothing logged.
   * Nulled in reAddGuaranteeLine()'s settle handler.
   */
  var reAddPending = null;

  /** How long the Checkout button will wait for a pending write. See below. */
  var CHECKOUT_WAIT_MS = 1200;

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /**
   * The properties the re-added line carries, so it is indistinguishable from
   * the one buildCartPayload() writes: the customer-visible note first (R5), then
   * the version.
   *
   * THE NOTE IS THE MERCHANT'S, READ OFF THE SAME WRAPPER AS THE OTHER TWO
   * SCALARS. readCartGuarantee() puts `data-rf-cart-guarantee-note` on the config
   * and this is its only consumer. The other side of that seam -- the
   * `guarantee_line_note` setting in THEME scope, so /cart and the order builder
   * read one string rather than two, and the Liquid that renders it onto the
   * wrapper -- belongs to the unit that moved it, and is pinned there rather
   * than asserted here.
   *
   * THIS HALF DOES NOT REQUIRE THAT HALF. An absent attribute is markup older
   * than the seam and a blank one is a merchant who emptied the field; both fall
   * back, so this shipped safely whichever order the two landed in.
   *
   * THE FALLBACK IS DECIDED THE SAME WAY THE BUILDER DECIDES IT, deliberately and
   * not by coincidence: trim, then treat empty as absent. Blank, absent AND
   * whitespace-only all fall back, so clearing the field in the theme editor cannot
   * strip a disclosure R5 requires -- on this path or on that one.
   *
   * ⚠ THIS PARAGRAPH ASSERTED THE EXPRESSION THE CODE NO LONGER USES, nine lines
   * above the comment correcting it, and it survived the round that fixed exactly
   * this shape elsewhere. It read: "`String(value || '') || GUARANTEE_NOTE`,
   * matching rf-order-payload.js's own `String(o.guaranteeNote || '') ||
   * GUARANTEE_NOTE`". ⚠ THE CORRECTION THEN GOT THE TWO DATES BACKWARDS, saying
   * "3 Sep 2026 and 10 Sep 2026 respectively" against the naming order (this file,
   * then rf-order-payload.js). Measured with `git log -S`: rf-order-payload.js moved
   * to a trim in 725e1af (3 Sep 2026); THIS file moved in 0b29d56 (10 Sep 2026), and
   * was the laggard, not the leader. The truthiness form treats '   ' as content.
   *
   * This comment said the opposite until 3 Sep 2026: that the note came from the
   * module's built-in "not from a setting", because the setting lived on the
   * ORDER BUILDER section and /cart could not read it. That was true when written
   * and is the divergence this seam removes.
   */
  function guaranteeLineProps(config) {
    var props = {};
    // TRIM-DECIDED, MATCHING rf-order-payload.js. This read
    // `String(config.note || '') || GUARANTEE_NOTE`, which treats '   ' as content
    // while the builder's `note.trim() !== ''` treats it as empty -- so a merchant
    // who cleared the textarea down to whitespace got `Membership: "   "` from this
    // path and the built-in sentence from the other. Two consequences, and the
    // second is what makes it this change's business: the R5 disclosure the
    // setting's own info promises ("it never goes silent") was stripped on this
    // path, AND the two writers' property sets differed -- which splits the very
    // merge key the shared selling-plan snippet exists to keep identical.
    var n = config.note == null ? '' : String(config.note);
    props[payloadMod.GUARANTEE_NOTE_KEY] =
      n.trim() !== '' ? n : payloadMod.GUARANTEE_NOTE;
    props._pricing_version = config.version;
    return props;
  }

  /**
   * The line of a given variant in a /cart/add.js response, or null.
   *
   * Shopify answers a multi-item add with `{ items: [...] }` and a single-item
   * add with the bare line object, so both shapes are read. MATCHED BY VARIANT
   * ID rather than taken as `items[0]`: the response is the only place the new
   * line's key exists, and a key aimed at the wrong line is the one thing the
   * unwind below must never emit.
   */
  function addedLine(data, id) {
    if (!data) return null;
    var list = Array.isArray(data.items) ? data.items : [data];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && Number(list[i].variant_id) === Number(id)) return list[i];
    }
    return null;
  }

  /**
   * ONE AT A TIME, AND AWAITABLE. The entry point every caller uses; the writes
   * are in writeGuaranteeLine() below.
   *
   * It returns the pending promise rather than a boolean's worth of nothing,
   * because the Checkout button needs something to wait on. A second call while
   * one is in flight gets the SAME promise back -- so boot() and a bfcache
   * restore firing within a tick produce one write, and a Checkout click in that
   * window waits on whichever of them is running.
   *
   * THE SETTLE HANDLER IS WHERE `reAddPending` IS NULLED, not a `finally` inside
   * the worker. A worker that threw synchronously -- before its first await --
   * would run its own `finally` BEFORE the assignment below and leave a settled
   * promise parked in the slot forever, which is a wedge rather than a guard.
   */
  function reAddGuaranteeLine(config) {
    if (!config) return null;
    if (reAddPending) return reAddPending;
    var p = writeGuaranteeLine(config);
    reAddPending = p;
    var settled = function () { if (reAddPending === p) reAddPending = null; };
    p.then(settled, settled);
    return p;
  }

  /**
   * Read the cart, decide, and write at most the Guarantee line.
   *
   * STANDS DOWN WHILE THE SUBMIT LOCK IS HELD, and this is not defensive
   * politeness -- it is a correctness rule. The builder's sequence is
   * clear.js -> add.js, and BETWEEN THE TWO the cart is empty. A /cart tab
   * evaluating in that window would see item_count 0 (so it stops at the gate)
   * or, a beat later, the freshly added lines with the Guarantee among them --
   * but a tab that read the cart just before clear.js and wrote just after add.js
   * would add a second Guarantee, the submitting tab's assertCart() would count
   * one line too many, and that customer would be shown "We could not confirm
   * your order."
   *
   * ── THE LOCK DOES NOT SPAN THAT WINDOW, AND THIS COMMENT USED TO SAY IT DID ──
   *
   * It was checked before the read and again before the write, under a claim that
   * "the lock is the only thing that spans that window". A check before a POST
   * spans nothing: the interleaving that costs a customer their order is
   * /cart checks and gets false -> the builder tab takes the lock and its
   * clear.js lands -> our add.js lands on the emptied cart -> the builder's
   * add.js MERGES with ours into Guarantee x2, because add.js merges on variant
   * PLUS properties and guaranteeLineProps() now writes the same two properties
   * the builder writes, off the same merchant string. assertCart() then fails on
   * the quantity, removeOurLines()
   * refuses a line at a quantity it did not ask for, and the customer is shown
   * "Our prices have just changed" over a cart holding a $0.00 line and a set of
   * cart-wide keys describing lines that no longer exist. Every word of that
   * message is false.
   *
   * SO THE THIRD CHECK IS THE ONE THAT MATTERS: the lock is re-read immediately
   * after the add settles, and if it is held NOW then the add landed inside the
   * forbidden window. One /cart/change.js at the key the add response returned
   * takes it back, and then it stops. NOT A RETRY LOOP: the add is not attempted
   * again, and neither is the unwind. A re-add that lost a race is a re-add the
   * NEXT load performs correctly, from a cart that has settled; retrying inside
   * the same losing window is how one duplicate becomes several.
   *
   * THE QUANTITY BRANCH NEEDS NO SUCH UNWIND, and that is a property of the
   * endpoint rather than an omission. Its changes are absolute quantities on
   * lines that already exist, so a clear.js landing under them leaves nothing for
   * change.js to hit and Shopify answers the missing key with an error that
   * cartError() reads -- which reaches the catch, which logs and stops.
   *
   * IT NEVER TAKES THE SUBMIT LOCK, only observes it. Taking it would make a page
   * load in one tab refuse a submit in another with "Another tab is already
   * placing this order", which would be false. It takes a SECOND lock of its own
   * instead (READD_LOCK_KEY, a few seconds), because `reAddPending` is per
   * document and two /cart tabs opened together will both read a Guarantee-less
   * cart and both POST -- $0.00 x 2 on the last screen before payment, self-
   * healing only on some later load.
   *
   * AND THE CART IT MEETS MAY BE HALF-UNWOUND. removeOurLines() now zeroes the
   * guarantee id too, so a customer landing on /cart after a STRANDED submit
   * arrives at whatever state that unwind actually reached.
   *
   * ── THIS PARAGRAPH USED TO END "Nothing special is done about it" ──────────
   *
   * That was defensible while the only way into a half-unwound cart was an
   * assertCart() mismatch on a cart this client had verified. It stopped being
   * defensible on 11 Sep 2026, when a failed add began falling into the same
   * unwind: the entry point became "we do not know what the origin did", and a
   * cart left holding real lines under the submit's own cart-wide keys satisfies
   * BOTH legs of the gate below. Nothing in THIS function changed. What changed
   * is what it SEES -- an unwind that wrote clears the version key in the same
   * POST as the zeros, so the gate closes on its own terms rather than on a
   * special case written here. Read removeOurLines()'s write site for why that
   * key and no other, and for why a refused unwind deliberately clears nothing.
   *
   * NO RELOAD AFTERWARDS. The line is hidden from /cart's render loop by variant
   * identity (sections/cart.liquid) and contributes 0 to the total, so the page
   * is already correct -- there is nothing to repaint, and a reload here would be
   * one condition away from a loop on the last screen before payment.
   */
  async function writeGuaranteeLine(config) {
    if (lockHeldElsewhere(LOCK_KEY, LOCK_TTL_MS)) return;
    if (lockHeldElsewhere(READD_LOCK_KEY, READD_LOCK_TTL_MS)) return;
    takeLock(READD_LOCK_KEY);
    try {
      var cart = await getCart();
      var action = guaranteeLineAction(cart, config);
      if (!action) return;
      // Re-checked across the await: the builder in another tab may have started
      // its clear.js -> add.js sequence while this read was in flight.
      if (lockHeldElsewhere(LOCK_KEY, LOCK_TTL_MS)) return;

      var out;
      var err;
      if (action.op === 'add') {
        // BUILT THE SAME WAY THE BUILDER BUILDS IT -- the key is ADDED, not set to
        // undefined -- so the two writers emit structurally identical lines and not
        // merely equal values. add.js merges on variant PLUS properties PLUS selling
        // plan, so any difference between these two shapes is a second $0.00 line.
        // `requestLine`, NOT `line`. `var line` is declared again below for the add
        // RESPONSE, and `var` is function-scoped -- two meanings of one identifier
        // separated only by position.
        var requestLine = {
          id: action.id,
          quantity: action.quantity,
          properties: guaranteeLineProps(config)
        };
        if (config.sellingPlan != null) requestLine.selling_plan = config.sellingPlan;
        out = await postJSON('/cart/add.js', { items: [requestLine] });
        err = cartError(out);
        // NO RETRY HERE EITHER -- see the submit path for why the submit-time
        // retry was removed outright. Lower stakes on this one regardless: nothing
        // was cleared first, so a refusal costs the re-added line rather than the
        // order, and R6 self-heals on the next /cart visit.
        if (err) throw new Error('guarantee_readd_failed: ' + err);

        // THE CHECK THAT IS ACTUALLY ATOMIC WITH THE WRITE. See the header.
        if (!lockHeldElsewhere(LOCK_KEY, LOCK_TTL_MS)) return;
        var line = addedLine(out.data, action.id);
        if (!line || !line.key) {
          throw new Error('guarantee_readd_unwind_failed: the add response named ' +
                          'no line of variant ' + action.id);
        }
        // BACK OUT EXACTLY OUR OWN CONTRIBUTION, by subtracting what we asked for
        // from what the response says the line holds. Created it: 1 - 1 = 0, and
        // the line goes. Merged into the builder's: 2 - 1 = 1, which is the
        // quantity the builder is about to assert -- so the arithmetic is right
        // in both readings of the race rather than only in the likely one.
        out = await postJSON('/cart/change.js', {
          id: line.key,
          quantity: Math.max(0, Number(line.quantity) - action.quantity)
        });
        err = cartError(out);
        if (err) throw new Error('guarantee_readd_unwind_failed: ' + err);
        return;
      }

      for (var i = 0; i < action.changes.length; i++) {
        out = await postJSON('/cart/change.js', {
          id: action.changes[i].id,
          quantity: action.changes[i].quantity
        });
        err = cartError(out);
        if (err) throw new Error('guarantee_readd_failed: ' + err);
      }
    } catch (e) {
      // LOG AND STOP. There is no error node on /cart and nothing the customer
      // could do about it, and every repair worth attempting would mean writing
      // something other than this one line. A missing Guarantee on an order is
      // recoverable by hand; a rewritten cart is not.
      console.error(e);
    } finally {
      releaseLock(READD_LOCK_KEY);
    }
  }

  /**
   * The /cart page's re-add config, or null.
   *
   * THREE SCALARS ON THE SECTION WRAPPER, not a JSON block and not the variant
   * map. The gate needs the guarantee variant id and the current
   * `_pricing_version`; guaranteeLineProps() needs the merchant's note. Nothing
   * else -- that is the point of gating on the version rather than on a count of
   * line types -- so shipping the map to a page with no builder on it would be a
   * second consumer bought for nothing.
   *
   * TWO OF THE THREE DECIDE WHETHER THERE IS A CONFIG AT ALL; the note does not.
   * A missing or blank note is a merchant who cleared the field, and
   * guaranteeLineProps() falls back to the module's built-in for exactly that --
   * refusing the whole re-add over it would let an empty text field remove a $0
   * line from every cart, which is a kill switch nobody asked for.
   *
   * ABSENT MEANS DO NOTHING, and it is absent on every page that is not /cart, on
   * /cart with the add-the-line switch off, and on /cart with the Guarantee
   * product unresolved. The switch is read in Liquid, BEFORE the product is
   * resolved, which is the same ordering the payload module uses and the same
   * reason: the kill switch must not depend on the picker being correct.
   */
  function readCartGuarantee(scope) {
    var node = (scope || document).querySelector('[data-rf-cart-guarantee]');
    if (!node) return null;
    var id = Number(node.getAttribute('data-rf-cart-guarantee-id'));
    var version = node.getAttribute('data-rf-cart-pricing-version') || '';
    var note = node.getAttribute('data-rf-cart-guarantee-note') || '';
    // THE FOURTH SCALAR, and it is DELIBERATELY OUTSIDE THE GUARD BELOW. A missing
    // selling plan is inert -- the shipped state, in fact -- and must never be a
    // reason to switch off the re-add. Only the id and the version are load-bearing
    // enough to refuse on, exactly as before.
    var plan = Number(node.getAttribute('data-rf-cart-guarantee-selling-plan'));
    if (!(isFinite(plan) && plan > 0 && Math.floor(plan) === plan)) plan = null;
    if (!id || !version) return null;
    return { id: id, version: version, note: note, sellingPlan: plan };
  }

  /* ── state, read from the DOM ─────────────────────────────────────────────
     Read here rather than shared from rf-order-builder.js. The DOM is the state
     in this theme, so a second reader cannot disagree with the first -- and the
     two files stay decoupled. */

  function readState(form) {
    var alb = form.querySelector('[data-rf-ob-albums]');
    var sets = form.querySelector('[data-rf-ob-sets]');
    var mode = form.querySelector('[data-rf-ob-mode]:checked');
    var usb = form.querySelector('[data-rf-ob-usb]:checked');
    return {
      // FLOOR 0 AND DEFAULT 0, both changed from 1 on 3 Sep 2026 -- and BOTH ARE
      // STILL 0 IN BOTH SWITCH POSITIONS. This function reads no variant map and
      // consults `allow_zero_album_orders` nowhere. That is a decision, not an
      // omission, and the two halves have separate reasons.
      //
      // THE FLOOR was the zero-album defect: while zero-album orders are allowed,
      // a customer sending loose photos alone had their 0 read back as 1 here, so
      // the payload carried an album line nobody asked for.
      //
      // AND IT MUST NOT BE RAISED TO `album_floor` NOW THAT ONE EXISTS. The count
      // this returns is the count buildCartPayload() tests the album minimum
      // against. Flooring it at 1 for the enforcing position would hand the guard
      // a 1 to inspect and it could never fire -- the same reason `albums` is left
      // unfloored in rf-order-payload.js, stated there as READ AFTER THE COUNT.
      // The floor is where the zero has to survive.
      //
      // THE DEFAULT IS A DIFFERENT ARGUMENT. This whole branch fires only when
      // the album field is ABSENT -- markup that failed to render, or a builder
      // that never booted, since the DOM read is the fallback for form.rfObState()
      // at the call site below. A default of 1 answers "the page is broken" with
      // "sell them one album", quietly, at $129. A default of 0 answers it with
      // {albums: 0, sets: 0}, which step 9b refuses by name. Refusing an order we
      // cannot read is the only defensible reading of an unreadable order, and it
      // is the same trade the ceiling check above makes.
      //
      // ⚠ AND THE REFUSAL IT REACHES IS `nothing_to_order: `, NOT
      // `albums_required: `, EVEN WHILE THE MINIMUM IS ON. Both counts default to
      // 0 here, so buildCartPayload() meets the empty-order throw first and the
      // album-minimum throw never runs. "We could not read your order" is
      // therefore a THIRD situation the `error_minimum` copy has to be true for,
      // beside the empty order and the missing album -- which is why that string
      // names none of the three and points at the counts instead.
      //
      // There was no case for 1: it was never a considered default, only the
      // clamp's floor written twice.
      albums: pricing.parseCount(alb ? alb.value : 0, 0),
      sets: pricing.parseCount(sets ? sets.value : 0, 0),
      usb: !!(usb && usb.value === 'yes'),
      mode: mode ? mode.value : 'deposit'
    };
  }

  function readMap(form) {
    // Section-scoped, matching rf-order-builder.js. Document-scoped until
    // 23 Aug 2026, and this is the more dangerous of the two: it is the map the
    // payload is BUILT from and the map assertCart VALIDATES against, so a second
    // builder instance would have validated a wrong cart against the same wrong
    // map and passed clean.
    var scope = (form && form.closest && form.closest('.shopify-section')) || document;
    var node = scope.querySelector('[data-rf-ob-variants]') ||
               document.querySelector('[data-rf-ob-variants]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /** Every variant id the catalogue offers. Anything else in the cart is a bug. */
  function allowedIds(map) {
    var ids = {};
    function add(entry) {
      if (!entry) return;
      if (entry.full && entry.full.id) ids[entry.full.id] = true;
      if (entry.deposit && entry.deposit.id) ids[entry.deposit.id] = true;
    }
    Object.keys(map.albums || {}).forEach(function (k) { add(map.albums[k]); });
    add(map.photoSet);
    add(map.usb);
    // The Guarantee contributes ONE id, not two: its full and deposit mirrors are
    // the same $0.00 variant, because zero is its own 90% and 25% mirror. add()
    // needs no special case for that -- it writes the same key twice.
    add(map.guarantee);
    return ids;
  }

  /* ── the assert ───────────────────────────────────────────────────────────
     Discriminating, not reassuring. Comparing a total alone proves little: the
     builder displays the map's price and adds the map's id, so an off-by-one
     count lookup passes a total comparison while quoting the wrong rate. The
     tripwire below is what catches that. */

  function assertCart(cart, state, payload, map) {
    var expected = payload.items;

    if (!cart || !Array.isArray(cart.items)) return 'the cart could not be read back';

    // COUNTED PER VARIANT, NOT PER LINE, since 2 Oct 2026. A line-item discount
    // that covers only PART of a line makes Shopify SPLIT it: buy-1-album-get-1-set
    // (`PK-KiveOffer`, production) on a 2-set order returns the photo set as TWO
    // lines of 1 -- one at $7.25, one at $0.00 -- and the add.js response already
    // carries the split. Measured on production and again on the dev store with an
    // identical code. Counted per line, that cart read as "4 lines, expected 3",
    // the cleanup refused it as a foreign quantity, and every such customer was
    // told the order might still be sitting in the cart. Nothing could check out.
    //
    // THE GUARD IS NOT WEAKENED. A foreign writer adding or removing our variants
    // still moves a per-variant SUM, an unexpected variant still moves the
    // distinct count, and a re-price still moves original_total_price below, which
    // is pre-discount and unaffected by the split. Per-line properties and selling
    // plans were never compared here, before this change or after it. What the split changes is how a quantity is laid
    // out across lines, which nothing downstream reads: the digest and every money
    // attribute are written from the payload, not from the cart.
    var qtyById = {};
    var distinct = 0;
    cart.items.forEach(function (line) {
      if (!Object.prototype.hasOwnProperty.call(qtyById, line.variant_id)) {
        qtyById[line.variant_id] = 0;
        distinct++;
      }
      qtyById[line.variant_id] += line.quantity;
    });

    if (distinct !== expected.length) {
      return 'the cart holds ' + distinct + ' products in ' + cart.items.length +
             ' lines, expected ' + expected.length + ' products';
    }

    // MATCHED BY VARIANT ID, NOT BY POSITION. Shopify does not preserve the order
    // of the `items` array: a payload of [album, photoSet, usb] came back as
    // [usb, photoSet, album]. Measured 23 Aug 2026 by docs/cart-contract.sh, which
    // caught this before it shipped -- a positional comparison would have failed
    // the assert on EVERY submit, zeroed the lines, and refused to sell.
    var ids = allowedIds(map);

    for (var i = 0; i < expected.length; i++) {
      var want = expected[i];
      if (!Object.prototype.hasOwnProperty.call(qtyById, want.id)) {
        return 'the cart is missing variant ' + want.id;
      }
      if (qtyById[want.id] !== want.quantity) {
        return 'variant ' + want.id + ' is quantity ' + qtyById[want.id] +
               ', expected ' + want.quantity;
      }
    }
    for (var j = 0; j < cart.items.length; j++) {
      if (!ids[cart.items[j].variant_id]) {
        return 'the cart holds variant ' + cart.items[j].variant_id +
               ', which is not in the catalogue';
      }
    }

    // original_total_price, NOT total_price: the latter is post-discount, so a
    // customer's own code would trip this every time.
    var charged = pricing.chargedCents(state, state.mode);
    if (cart.original_total_price !== charged) {
      return 'the cart charges ' + cart.original_total_price + ' cents where the page showed ' + charged;
    }

    // THE TRIPWIRE. The rendered map and perAlbum() are two sources for the same
    // number by design (plan decision D1), guarded by a test and by a networked
    // checker. This is the third layer: drift cannot reach checkout, only fail
    // loudly here.
    //
    // GATED ON state.albums > 0 since 3 Sep 2026, mirroring the photo-set and USB
    // tripwires immediately below, which have always been gated on their own
    // component being bought. It ran unconditionally, which was correct while
    // every order carried an album line and is wrong now that a loose-photos-only
    // order is sellable: curvePointFor(0) is 1, so a zero-album cart looked up
    // map.albums['1'] -- a variant nobody asked for -- and refused the sale with
    // 'no variant data for 1 albums' on any map that legitimately lacks a 1-album
    // entry. Same reasoning as buildCartPayload()'s "RESOLVED INSIDE THE GATE".
    //
    // THE GATE DOES NOT WEAKEN THE GUARD. Above zero the tripwire is byte-for-byte
    // what it was, and the branch it skips is one whose subject the cart does not
    // contain: with no album line there is no rate to have drifted and no
    // _list_unit_price_cents written from one. tests/order-builder-cart.test.js
    // control-runs exactly that -- a >=1-album cart with a drifted list price
    // still refuses, through this gate.
    if (state.albums > 0) {
      var point = pricing.curvePointFor(state.albums);
      var entry = map.albums && map.albums[String(point)];
      if (!entry) return 'no variant data for ' + point + ' albums';
      if (entry.listCents !== pricing.albumListRateCents(state.albums)) {
        return 'the rendered rate for ' + point + ' albums is ' + entry.listCents +
               ' but the curve says ' + pricing.albumListRateCents(state.albums);
      }
    }

    // THE SAME TRIPWIRE FOR THE OTHER TWO COMPONENTS. It covered albums only until
    // 23 Aug 2026, which left exactly the gap docs/check-catalogue.sh had deferred
    // to this unit: that script cannot read metafields, said so, and pointed here.
    // A dropped `photokive.list_price` renders `listCents: 0` through Liquid's
    // `| default: 0`, and nothing noticed -- the page advertised $0 per set beside
    // a $29.00 summary written from the constant, and the 0 rode onto the order as
    // `_list_unit_price_cents`, which is what Kive recomputes the balance from. The
    // charged total still matched, so the comparison above passed clean.
    if (state.sets > 0 && map.photoSet &&
        map.photoSet.listCents !== pricing.LOOSE_SET_CENTS) {
      return 'the rendered photo-set price is ' + map.photoSet.listCents +
             ' but the catalogue says ' + pricing.LOOSE_SET_CENTS;
    }
    if (state.usb && map.usb && map.usb.listCents !== pricing.USB_LIST_CENTS) {
      return 'the rendered USB price is ' + map.usb.listCents +
             ' but the catalogue says ' + pricing.USB_LIST_CENTS;
    }

    // AND THE SAME TRIPWIRE FOR THE GUARANTEE, whose catalogue price is 0.
    //
    // GATED ON THE ADD-THE-LINE SWITCH SINCE 3 SEP 2026 (U11), and this comment
    // used to say the opposite. It read "UNGATED because every order carries the
    // line (R4)", which was true when it was written and stopped being true one
    // unit later: U10 moved `guarantee_line_enabled` into theme settings, so a
    // merchant can now turn the line OFF and every order legitimately carries no
    // Guarantee at all. Ungated, the two refusals below then fired on a cart that
    // was never supposed to hold the line -- narrowly for the second one (it needs
    // a wrong photokive.list_price on the right SKU) and store-wide for the first
    // (any map rendered with a null entry). Both are the failure the kill switch
    // exists to prevent: a cleared or mis-aimed product picker taking the store
    // offline behind a refusal.
    //
    // `map.guaranteeLine !== false` IS THE SAME EXPRESSION buildCartPayload() uses
    // to decide whether to add the line at all -- deliberately the same read of
    // the same key, not a second statement of the rule. `!== false` rather than a
    // truthiness test because a map rendered before the switch existed carries no
    // such key, and that map means "on".
    //
    // SO THIS IS NOW THE SAME SHAPE AS THE THREE TRIPWIRES ABOVE: each is gated on
    // its own subject being in the order. Theirs is a count or a checkbox on the
    // page; this one's is the switch. Inside the gate the checks are byte-for-byte
    // what they were, which is why the absent entry stays a REFUSAL here rather
    // than a skip -- with the line switched on, R4 puts it on every order, so a
    // map that has lost the entry disagrees with the contract.
    //
    // WHY IT EARNS ITS PLACE when resolve() already refuses a bad entry at build
    // time. resolve()'s mirror check catches the same misconfiguration one step
    // earlier and says "the Photokive Guarantee is priced 9588 but the full mirror
    // of 0 is 0" -- true, and unreadable as "the Guarantee product setting points
    // at the annual variant". This names the setting. It is the third layer, in
    // the same sense the album tripwire above is: drift cannot reach checkout, it
    // can only fail loudly here.
    //
    // COMPARED AGAINST 0, WHICH IS THE CATALOGUE, not against the line's price. A
    // dropped photokive.list_price on the $0 variant renders 0 through Liquid's
    // `| default: 0` and 0 is the right answer here, so this cannot false-alarm
    // the way the photo-set tripwire is designed to true-alarm. What it catches is
    // a map entry built from a variant that costs something.
    if (map.guaranteeLine !== false) {
      if (!map.guarantee) {
        return 'the variant map carries no Guarantee entry, so the $0.00 Guarantee ' +
               'line cannot be checked — set the Guarantee product in the theme editor';
      }
      if (map.guarantee.listCents !== 0) {
        return 'the rendered Guarantee list price is ' + map.guarantee.listCents +
               ' but the Guarantee line is $0.00 — the Guarantee product setting is ' +
               'pointing at the wrong product or the wrong variant';
      }
    }

    // A cart-level discount the builder cannot clear. This store's policy is zero
    // discounts, so an AUTOMATIC one appearing is a configuration change, not a
    // customer action, and it would otherwise present as a bare total mismatch for
    // every customer at once.
    //
    // FILTER ON `type` -- do not remove it. cart_level_discount_applications holds
    // BOTH kinds, and an "amount/percentage off order" code is what
    // photokivepricing.md calls the standard promotion. Until 23 Aug 2026 this
    // rejected on the array merely being non-empty, so a customer arriving from
    // /discount/<code> had the sale refused and their cart zeroed -- the hostility
    // R4 exists to forbid, and the opposite of what this file's header claims. The
    // old comment reasoned correctly that an automatic discount does not appear in
    // `discount_codes`, but that is a different array from the one read here.
    // Found by ce:review; covered by tests/order-builder-cart.test.js.
    var cartLevel = (cart.cart_level_discount_applications || []).filter(
      function (a) { return a && a.type !== 'discount_code'; }
    );
    if (cartLevel.length) {
      return 'an automatic discount is active on this store, which this page cannot ' +
             'account for. Nothing was charged.';
    }

    // Attributes ride the add.js call, which is undocumented behaviour. Verify
    // rather than assume; the caller falls back to update.js.
    // Empty values are skipped, and that is not laziness: Shopify normalises an
    // attribute written as '' to DELETED, so "present but empty" is not a state
    // the cart can hold. Writing '' is exactly how the retired-keys list removes a
    // stale key -- verified in docs/cart-contract.sh.
    var got = cart.attributes || {};
    var missing = Object.keys(payload.attributes).filter(function (k) {
      return payload.attributes[k] !== '' && !got[k];
    });
    if (missing.length) return 'ATTRIBUTES_MISSING:' + missing.join(',');

    return null;
  }

  /* ── the /cart re-add (R6) ────────────────────────────────────────────────
     A customer who removes the $0.00 Guarantee line -- which they can only do by
     crafting a POST, because no surface in this theme renders a remove control --
     gets it back on their next visit to /cart. The decision is here; the writes
     are in the transport section below.

     WHY IT LIVES IN THIS FILE rather than in a fourth asset. Four things it needs
     are module-private and none of them wants a second owner: the cross-tab lock
     (LOCK_KEY and its helpers -- a second module would re-declare the key, which
     is two sources of truth for a lock, and it has to OBSERVE that key rather
     than merely hold one of its own), the transport layer (route(), postJSON(),
     getCart(), the deadline, and cartError()'s handling of Shopify's
     200-with-`status` Cart Error), the module-level listener registrations --
     the SINGLE `pageshow`, whose one-listener discipline is a written rule in
     this file and is pinned by tests/listener-placement.test.js, and the
     delegated `submit` that holds the Checkout button while a write is pending --
     and the CommonJS export block that lets the decision be proved under
     `node --test` with no browser.

     WHAT IT MUST NEVER DO is in the executor's comment, not here: this path
     writes the Guarantee line and nothing else, on every branch. */

  /**
   * WHAT /cart SHOULD WRITE, IF ANYTHING. Pure: a live cart object and the
   * page's config in, one action or `null` out. assertCart() above is the
   * precedent and the shape is deliberately the same -- plain data in, a verdict
   * out, no fetch, no DOM, no clock -- because this is the decision that can add
   * a line to a stranger's cart and it has to be provable without a browser.
   *
   *   null                                        do nothing
   *   { op: 'add', id, quantity: 1 }              POST /cart/add.js
   *   { op: 'quantity', changes: [{id, quantity}] }  POST /cart/change.js, per entry
   *
   * `changes[].id` is always a LINE KEY -- or, for a cart.js payload that somehow
   * carries no key AND holds exactly one line of the Guarantee variant, that
   * variant id -- taken from a line of the Guarantee variant in the very cart
   * being judged. Nothing else can appear there; a keyless line among SEVERAL is
   * skipped outright, for the reason in the loop at the foot of this function.
   * That is what makes "this path writes only the Guarantee line" checkable at
   * the decision layer rather than only at the call site.
   *
   * ── THE GATE IS `_pricing_version`, AND IT IS A FILTER, NOT A CONTROL ───────
   *
   *   cart.attributes._pricing_version === config.version && cart.item_count > 0
   *
   * READ THAT AS A FILTER AND NOTHING MORE. This block claimed until 3 Sep 2026
   * that a current-version cart "was by construction written by a builder that
   * already refused anything under the minimum", and that permalink,
   * `/cart/add?id=` and pre-feature carts "carry no `_pricing_version` or an old
   * one". Both claims are false, and reviewers drove them:
   *
   *   - `_pricing_version` IS CUSTOMER-WRITABLE. docs/product-spec.md §9
   *     enumerates it by name in the list of keys anyone can rewrite with one
   *     unauthenticated `/cart/update.js` POST, and records that the itemised
   *     form of that list had already failed once by omitting this very key.
   *   - IT SURVIVES clear.js, which does not clear cart-level keys
   *     (docs/solutions/best-practices/cart-api-behaviours-the-docs-omit-2026-08-23.md).
   *   - THE VERSION WAS BUMPED ON 9 Sep 2026 (§6c.3), off the previous
   *     `2026-08b` literal and on to `curve-2026-09a`. ⚠ THIS BULLET SAID THE
   *     OPPOSITE and its reasoning inverted rather than its string changing. It
   *     read "AND THE VERSION WAS NOT BUMPED IN THIS PASS ... so a cart written
   *     before any of this shipped satisfies the gate exactly." A pre-bump cart
   *     now carries the old string and the gate REJECTS it, so that particular
   *     route in is closed.
   *     ⚠ THE OLD LITERAL IS DELIBERATELY NOT SPELLED OUT IN FULL ANYWHERE IN
   *     `theme/` OR `tests/`. §6c.3's sign-off criterion is that
   *     `grep -rn <old literal> theme/ tests/` returns NOTHING, so writing it
   *     here -- even inside a comment, even to document the bump -- would
   *     permanently fail the gate's own check. That is why this reads `2026-08b`
   *     rather than the whole string. The full before/after is in
   *     `docs/launch-day-checklist.md` §6c.3 and in this commit's message.
   *     **The gate is still not a provenance check, and that is the point of this
   *     block** -- by the first bullet the value is CUSTOMER-WRITABLE, so anyone
   *     can write `curve-2026-09a` into any cart with one unauthenticated
   *     /cart/update.js POST. The bump removed an accident, not the mechanism.
   *
   * So a USB-only cart carrying the CURRENT version -- written by the builder or
   * forged by the customer, this function cannot tell -- returns `{op:'add'}`, and
   * so does a cart holding one foreign variant. Both are pinned as such in
   * tests/order-builder-cart.test.js. The gate narrows what this touches; it
   * decides nothing about whether the cart deserves to be touched.
   *
   * WHAT MAKES THAT SAFE IS THE WRITE, NOT THE GATE. The line is $0.00, so no
   * total moves, and rf-order-payload.js excludes it from `_lines_digest` BY
   * VARIANT IDENTITY -- so adding it changes neither what the digest names nor
   * what Kive recomputes against it. It carries nothing that moves money. Any
   * future edit that makes this path write something which does is not covered by
   * this argument and needs its own.
   *
   * TWO THINGS THE GATE IS STILL WORTH HAVING FOR:
   *
   * 1. LINE-ITEM PROPERTIES CANNOT TELL A PHOTO SET FROM A USB. Both carry only
   *    `Payment`, `_list_unit_price_cents` and `_pricing_version` -- there is no
   *    component name on a line. So enforcing R6's ">=1 album OR >=1 photo set"
   *    by counting would mean shipping the whole variant map to /cart and matching
   *    ids against it: a second consumer of the map, on a page that has no
   *    builder, kept in step by nothing. Since the gate is a filter, the ">=1
   *    album OR >=1 photo set" precondition is STILL NOT ENFORCED HERE, IN EITHER
   *    POSITION -- and neither is the album minimum `allow_zero_album_orders`
   *    stacks on top of it. Both are enforced at submit (step 9b,
   *    minimumRefusal() below), which is where they are knowable, and this page
   *    re-derives neither. A cart built while the switch was on keeps its
   *    checkout button after the switch goes off; that is a fact about
   *    server-side carts, not a hole in this gate.
   *
   * 2. IT KEEPS THE ORDINARY UNRELATED CART OUT. A `/cart/add?id=` cart or a
   *    permalink cart written by someone who never met the builder carries no
   *    `_pricing_version`, so it is left alone -- not because it could not be
   *    written safely, but because there is no reason to write it.
   *
   * `item_count > 0` is the other half of R6's "never create a $0-only cart": an
   * empty cart is left empty, so a $0.00 line can never be the only thing in it.
   * A cart that ALREADY holds the Guarantee alone is a cart this function did not
   * create and does not add to -- at most it corrects the quantity, which cannot
   * turn a non-$0 cart into a $0 one.
   *
   * QUANTITY-AWARE, because /cart/add.js is ADDITIVE. A presence-only check that
   * ran twice -- one boot, one bfcache restore -- would leave `$0.00 x 2` on the
   * last screen before payment. Present at quantity 1 is the only state that
   * needs no write.
   */
  function guaranteeLineAction(cart, config) {
    // A page that rendered no config (not /cart, the switch off, or the product
    // picker unresolved) decides nothing. Checked before the cart so the absent
    // case costs no reasoning about carts.
    if (!config || !config.id || !config.version) return null;
    if (!cart || !Array.isArray(cart.items)) return null;

    // THE GATE.
    if (!(Number(cart.item_count) > 0)) return null;
    var attrs = cart.attributes || {};
    if (attrs._pricing_version !== config.version) return null;

    // OUR LINES, BY VARIANT IDENTITY -- never by price and never by title, the
    // same anchor sections/cart.liquid uses to hide the line and
    // rf-order-payload.js uses to keep it out of the digest. A repriced or
    // renamed variant cannot slip past this, and a legitimately $0.00 order line
    // cannot be mistaken for it.
    var ours = [];
    for (var i = 0; i < cart.items.length; i++) {
      if (Number(cart.items[i].variant_id) === Number(config.id)) ours.push(cart.items[i]);
    }

    if (!ours.length) return { op: 'add', id: config.id, quantity: 1 };
    if (ours.length === 1 && Number(ours[0].quantity) === 1) return null;

    // Exactly one line at quantity 1 is the target state. The first of our lines
    // is kept and set to 1; any further line of the SAME variant -- which only a
    // crafted POST with different properties can produce, since add.js merges on
    // variant plus properties -- is zeroed. Every id here is one of our own line
    // keys, by construction of `ours`.
    //
    // THE `key || variant_id` FALLBACK IS NOW SCOPED TO ours.length === 1, and a
    // keyless line among several is SKIPPED. /cart/change.js resolves a bare
    // variant id to the FIRST matching line, so with two Guarantee lines and no
    // key every emitted change carried the same id: change #2 undid change #1,
    // the duplicate survived, and the identical pair was rewritten on every
    // subsequent load. With exactly one line of the variant, "the first matching
    // line" and "our line" are the same line and the fallback is exact. A change
    // aimed at the wrong line is strictly worse than no change at all on a path
    // whose entire safety argument is that it touches no line but the Guarantee.
    var changes = [];
    for (var j = 0; j < ours.length; j++) {
      var want = j === 0 ? 1 : 0;
      if (Number(ours[j].quantity) === want) continue;
      var target = ours[j].key || (ours.length === 1 ? ours[j].variant_id : null);
      if (!target) continue;
      changes.push({ id: target, quantity: want });
    }
    return changes.length ? { op: 'quantity', changes: changes } : null;
  }

  /**
   * THE MINIMUM ORDER, ASKED OF THE MODULE THAT OWNS IT (R2).
   *
   * Returns the Error to surface, or null. Pure: it builds a payload and throws
   * it away.
   *
   * WHY IT DOES NOT CALL isUnsellable(). rf-order-builder.js holds the single
   * statement of "unsellable" -- zero albums and zero photo sets, both read
   * through pricing's own clamps -- and it exports that function. Through the
   * CommonJS hatch ONLY. It publishes no browser global, which is checkable
   * rather than taken on trust:
   *
   *   grep -n 'root\.Photokive[A-Za-z]* *=' theme/assets/*.js
   *
   * Two hits, both `= api` in a UMD wrapper: rf-pricing.js and
   * rf-order-payload.js. rf-order-builder.js has none. So the pinned load order
   * in layout/theme.liquid does put that file ahead of this one and still leaves
   * this one with nothing to call. (Do NOT run that grep as `window.Photokive`
   * instead -- every consumer READS one of the two globals, rf-order-builder.js
   * included, so the wider pattern reports the opposite of what this says.)
   *
   * The remaining option would be to restate the predicate here, and that is the
   * one thing this must not do. It would be a THIRD copy of a rule whose own
   * header block in rf-order-builder.js hands the reader a grep for its copies
   * and states how many to expect -- a count in a file this one cannot edit, so
   * the copy would be silent and the count would be wrong. (Which is why the
   * predicate is not written out in this comment either, in code or in prose.)
   *
   * So instead this asks the module that already refuses the order:
   * buildCartPayload() in rf-order-payload.js throws on exactly that condition,
   * with the `nothing_to_order: ` prefix its own comment calls load-bearing.
   *
   * NARROWED TO THAT ONE PREFIX, DELIBERATELY. buildCartPayload also throws on an
   * unknown payment mode, on the self-serve ceiling, and on a variant the map does
   * not carry. Those keep the route they have always had -- through run(), its
   * failure record and its error copy -- because widening a minimum-order check
   * into a general pre-flight would move three unrelated failures to a different
   * message and a different point in the sequence, silently, under this unit.
   *
   * Building the payload twice per submit is the cost, and it is arithmetic and
   * object construction with no fetch, no DOM and no Math.random in it (that is
   * the standing constraint on rf-order-payload.js, stated in its header). The
   * per-submit token is not generated here at all: run() makes its own.
   */
  function minimumRefusal(state, map) {
    try {
      payloadMod.buildCartPayload(state, map);
    } catch (e) {
      var m = String(e && e.message ? e.message : e);
      // TWO PREFIXES NOW, AND STILL NARROW. ALBUM-MIN U2 added `albums_required: `,
      // album-minimum refusal. It is listed rather than matched loosely for the
      // reason the block above gives: the unknown-mode, ceiling and missing-variant
      // throws must keep the route they have always had. Widening by one named
      // prefix keeps that property; widening by a pattern would not.
      if (m.indexOf('nothing_to_order: ') === 0) return e;
      if (m.indexOf('albums_required: ') === 0) return e;
    }
    return null;
  }

  /* ── the run ──────────────────────────────────────────────────────────── */

  function token() {
    // Not from the payload module, which is deliberately free of Math.random so
    // its output stays assertable.
    return Math.random().toString(36).slice(2, 10);
  }

  async function run(form, state, map, attempt) {
    var opts = {
      token: token(),
      environment: form.getAttribute('data-rf-ob-environment') || '',
      // R5. The Guarantee line's one customer-visible property, read here rather
      // than out of the variant map: that map is a JSON <script> block, and its own
      // header records why merchant free text is kept out of it -- the HTML parser
      // closes the block at the first literal `</script`, inside a JSON string
      // included. An attribute is the same route every other merchant string on
      // this form takes. Blank or absent falls back to the module's built-in, so
      // clearing the field in the theme editor cannot strip a disclosure R5
      // requires; rf-order-payload.js owns that fallback.
      guaranteeNote: form.getAttribute('data-rf-ob-guarantee-note') || '',
      priorFailures: readFailures()
    };
    var payload = payloadMod.buildCartPayload(state, map, opts);

    // ── THE HEARTBEAT, BEFORE EVERY CART CALL FROM HERE ──────────────────────
    // Up to seven calls on the longest path, each ALLOWED 15 seconds, against a
    // 30-second TTL -- derived, not measured; the enumeration and that caveat are
    // in the lock block below. Two calls that each ran to their deadline are
    // already 30 seconds, so without a re-stamp a tab that is genuinely mid-write
    // looks expired to the next tab, which is then admitted, calls clear.js, and
    // empties the cart this one has already asserted. The touch is a no-op unless
    // this document still owns the record -- see its header for why resurrecting
    // someone else's lock would be the worse bug.
    touchLock();
    var cleared = await postJSON('/cart/clear.js', null);
    var clearErr = cartError(cleared);
    if (clearErr) {
      // ABORT. Adding now would leave the previous configuration in the cart
      // alongside the new one.
      throw new Error('clear_failed: ' + clearErr);
    }

    touchLock();
    // ── THE ADD IS WRAPPED, AND THE CATCH IS NOT A RETRY ─────────────────────
    // It was a bare `await` until 11 Sep 2026, so a rejection -- a dropped
    // connection, or the abort deadline() arms at fifteen seconds -- propagated
    // straight out of this function, past the verify block AND past the cleanup,
    // and the customer was told nothing had been charged over a cart that may hold
    // the whole order. Nothing is re-sent here. What the catch does is record WHY,
    // so control reaches the same verify block every other failure reaches.
    var addFailure = null;    // why the add failed, in Shopify's words or in ours
    var addCopy = '';         // Shopify's own shopper-facing wording, where it sent any
    var addUnsettled = false; // the Cart API returned NO DECISION about this batch
    try {
      var added = await postJSON('/cart/add.js', {
        items: payload.items,
        attributes: payload.attributes
      });
      addFailure = cartError(added);
      addCopy = shopperText(added);
      // ── THE AXIS IS "DID THE CART API RETURN A DECISION", NOT "DID THE PROMISE
      // REJECT" ──
      //
      // This flag was set in the catch below and NOWHERE ELSE, which keyed it on a
      // property of the CLIENT'S TRANSPORT rather than of the ORIGIN'S STATE, and
      // it was wrong in both directions at once.
      //
      //   A BODY-CARRIED CART ERROR IS A DECISION. `data.status` is Shopify
      //   answering "I evaluated this batch and refused it" -- the items array is
      //   ATOMIC, so nothing of ours landed. Measured, and recorded in
      //   docs/solutions/best-practices/cart-api-behaviours-the-docs-omit-2026-08-23.md
      //   section 2. Until now that field was read only to pick error text.
      //
      //   A BARE NON-OK STATUS IS NOT. `fetch` resolves for EVERY HTTP status, so
      //   a 504 -- the edge giving up while the origin may still be applying the
      //   batch -- took this branch, was scored settled, and the customer was told
      //   "Nothing has been charged" over a cart that was about to hold the whole
      //   order. That is precisely the end state this flag was built to prevent,
      //   on a class it did not cover. An unparseable body is the same: no body,
      //   no decision.
      //
      // ⚠ AND USING THE ATOMIC CLASS HERE IS NOT THE RETRY COMING BACK. The rule
      // deleted three times used atomicity to AUTHORISE A RE-SEND, which needs
      // "did the failed write land?" -- a question a browser cannot answer. This
      // uses it to WITHDRAW A CLAIM: the strand warning is not made when the
      // origin itself said the batch was refused. Nothing is re-sent on any
      // branch, and the unwind still goes out either way (D2).
      addUnsettled = !!addFailure && !(added.data && added.data.status);
    } catch (addRejection) {
      // THE ONE BRANCH IN THIS FILE THAT TURNS ON TRANSPORT RATHER THAN ON WHAT
      // SHOPIFY SAID, and it has to be that rather than an error class: abort() is
      // client-side and does not stop the origin, so a request that never answered
      // can still be applied after we have given up on it. Everything downstream
      // treats the three failure classes identically except for this flag.
      addUnsettled = true;
      addFailure = 'the add could not be sent: ' +
                   ((addRejection && addRejection.message) || 'transport failure');
    }

    // THE SELLING PLAN IS ADVISORY AT RENDER TIME, AND ONLY AT RENDER TIME.
    //
    // The id is resolved in Liquid when the page RENDERS and this POST happens when
    // the customer SUBMITS, from whatever variant map the open tab is holding. So a
    // plan that was live at render can be dead by now -- the app that owns the group
    // was uninstalled, the group was disassociated from the variant, the allocation
    // changed. No render-time guard reaches any of that.
    //
    // WHAT THAT COSTS, MEASURED against the real cart 10 Sep 2026: an unapplicable
    // plan answers {"status":422,"description":"Cannot apply selling plan to
    // variant"} and the items array is ATOMIC, so item_count is 0 -- the album,
    // photo-set and USB lines do not land either. This runs AFTER clear.js, so that
    // customer is left with an empty cart and Shopify's raw description.
    //
    // -- THERE IS DELIBERATELY NO SUBMIT-TIME RETRY, AND THREE ROUNDS OF ONE WERE
    // REMOVED TO GET HERE. DO NOT RE-ADD ONE WITHOUT READING THIS. --
    //
    // A retry has to answer "did the failed write actually land?", because add.js is
    // additive and re-sending a landed batch DOUBLES every chargeable line. The
    // browser cannot answer it. The only instrument is a /cart.js read; it fails in
    // the same second as the failure it is diagnosing, and a read taken before the
    // origin applies the write is FRESH AND WRONG -- no cache directive helps,
    // because the defect is ordering, not staleness. Each of the three attempts
    // shipped a different money defect, each found by the review of the one before:
    //   1. retried on any error -- a lost response after a landed write doubled
    //      every chargeable line;
    //   2. probed before every retry -- so during the store-wide outage the retry
    //      exists for, a probe blip lost orders version 1 would have placed;
    //   3. split by error class and probed only the ambiguous one -- and the probe
    //      that reads an empty cart a moment before the origin applies the write
    //      still doubles it, after which removeOurLines() REFUSES to unwind, because
    //      a line at an unexpected quantity is exactly what its foreign-writer guard
    //      protects. Doubled, live, and unremovable by the code that made it.
    //
    // SO THE GUARD IS ENTIRELY AT RENDER TIME. snippets/guarantee-selling-plan.liquid
    // refuses to emit any id that is not on this variant's allocations AT $0.00, so
    // the only way to reach the failure above is a tab rendered before the plan died.
    // That order fails honestly, nothing is charged, and a reload fixes it. That is
    // the same degradation R4 already prefers, and
    //
    // ⚠ READ THE NEXT BLOCK BEFORE REPEATING THAT SENTENCE ANYWHERE ELSE. Since
    // 11 Sep 2026 it is a claim about the CHARGE and not about the CART. The
    // failure above no longer exits here: it falls into the cleanup below, and
    // where the cleanup cannot prove the cart is empty this customer is told the
    // cart may still be holding the order instead. Two shipped files carried the
    // shorter version of this sentence -- the snippet named below and
    // assets/rf-order-payload.js -- and both now carry the longer one, because a
    // sentence that is true of the charge and read as a fact about the cart is
    // exactly the shape this file has already paid for once.
    //
    // docs/guarantee-subscription-handoff.md sec 2 already requires everything
    // downstream to tolerate a missing line ("a $0.00 line is not a contract, and its
    // absence is not a decline").
    //
    // RE-OPEN THIS ONLY when a selling plan group exists on the store and the whole
    // path can be driven end to end against a real one. None of the three attempts
    // could be -- the store has zero plan groups -- and that is why none was safe.

    // -- AND THE UNWIND BELOW IS NOT THAT RETRY, THOUGH IT WILL READ LIKE ONE --
    //
    // A failed add no longer exits here. It falls THROUGH to the verify block and
    // the cleanup, which means a second write on the money path -- which is what
    // all three removed rounds were. So the difference is stated rather than
    // assumed. It rests on TWO premises, and only one of them is measurable:
    //
    //   (a) the write the cleanup issues is ABSOLUTE, so sending it against a cart
    //       that never received the add changes nothing. MEASURED 11 Sep 2026,
    //       Shopify CLI 4.8.0, through `shopify theme dev` on :9292, recorded in
    //       docs/cart-contract.sh R10 Q1 and Q2:
    //         "Q1 `updates` is ABSOLUTE: a line of 2 set to 1 reads 1   [a delta
    //          would read 3]"
    //         "Q2 zeroing a REAL variant that is ABSENT from the cart is ACCEPTED
    //          -- the body is a cart, not a Cart Error"
    //         "Q2 and it is a NO-OP: the cart is unchanged"
    //       Set beside the row this file's own history turns on, section 6 of the
    //       same script: "quantities DOUBLE on a second add   [24 -> 48, which is
    //       what the guard prevents]". Absolute against additive is the whole of
    //       the difference between the cleanup and the thing deleted three times.
    //
    //   (b) the add's fate is SETTLED by the time those zeros are applied, so no
    //       third outcome arrives afterwards. NOT MEASURABLE FROM A BROWSER, and
    //       simply false for a request that was aborted or dropped.
    //
    // So the cleanup escapes the rule that killed the retry for a request Shopify
    // ANSWERED, and does not escape it for one it did not. The resolution is NOT to
    // skip it there -- it still removes whatever is present, and still fails safe.
    // It is `addUnsettled`, which makes the outcome strand-class however clean the
    // cleanup comes back: the customer is told the cart may still hold the order,
    // which is true whether or not it ever lands. The split is at the foot of this
    // function; the reason the zeros themselves are safe to send is at their site.

    // ── FROM HERE THE CART HOLDS CHARGEABLE LINES ────────────────────────────
    // Every exit below has to either confirm them or take them back out. The
    // whole block is wrapped because the read itself can fail: getCart() used to
    // be unguarded and its rejection propagated straight past the cleanup, so a
    // dropped connection left a full, never-verified cart reachable via
    // /checkout, browser history and Shopify's abandoned-checkout email -- behind
    // a message telling the customer nothing had been charged.
    //
    // AN UNVERIFIED CART IS TREATED AS A WRONG ONE. We cannot tell them apart, so
    // the safe reading is the pessimistic one.
    //
    // AND SINCE 11 Sep 2026 A FAILED ADD ARRIVES HERE TOO, rather than exiting
    // above this banner. `problem` is seeded from the add's own reason, which does
    // three things at once: it makes the block below run, it keeps the reason the
    // CUSTOMER sees (Shopify writes `description` for shoppers) rather than the
    // cart's account of the damage, and it hands the cleanup a REAL cart to compare
    // against instead of a null -- which is what arms the quantity guard on the one
    // path most exposed to the theme's other cart writer.
    var problem = addFailure;
    // DECLARED HERE, NOT WITH `var` INSIDE THE try. It was, and `var` hoists to
    // the function rather than the block, so the reference below still resolved
    // -- to `undefined` -- on any path where getCart() threw. removeOurLines()
    // then ran with no cart and skipped its quantity comparison entirely, which
    // disarmed that guard on precisely the read-failure path most likely to
    // coincide with another tab writing. Its lock guard is armed either way now,
    // and this makes the second one honest about what it has.
    var cart = null;
    // THE HEARTBEAT SITS ABOVE THE try, NOT INSIDE IT. A static pin matches the
    // try's first statement at exact indentation, because `var cart` was once
    // declared in there and hoisted away the unwind's other guard; a statement
    // added between the two would re-open that reading.
    touchLock();
    try {
      cart = await getCart();
      // THE ADD'S OWN REASON OUTRANKS THE CART'S ACCOUNT OF IT, and this guard is
      // load-bearing in both directions. Downward: 'the cart holds 0 products in
      // 0 lines, expected 4 products' is a worse sentence to show a customer than the one Shopify
      // wrote. Upward, and this is the dangerous one: on a rejected add whose write
      // LANDED before this read, assertCart() answers null -- a perfect cart -- and
      // an unguarded assignment would clear `problem`, skip the cleanup and send
      // that customer to checkout on an order this client never had confirmed.
      if (!problem) problem = assertCart(cart, state, payload, map);

      // The one recoverable assert failure: attributes did not ride the add. Write
      // them explicitly and re-check, rather than refusing an otherwise good cart.
      if (problem && problem.indexOf('ATTRIBUTES_MISSING:') === 0) {
        touchLock();
        var written = await postJSON('/cart/update.js', { attributes: payload.attributes });
        var writeErr = cartError(written);
        touchLock();
        cart = await getCart();
        problem = assertCart(cart, state, payload, map);
        // Do not report a bare attribute mismatch when the write is what failed.
        if (problem && writeErr) problem = 'attribute write rejected: ' + writeErr;
      }
    } catch (err) {
      if (!problem) {
        problem = 'the cart could not be verified: ' +
                  (err && err.message ? err.message : 'read failed');
      }
    }

    if (problem) {
      var cleanup = await removeOurLines(payload, cart);
      // ── THE SPLIT, AND THE ONE RULE IN IT THAT IS NEW ──────────────────────
      //
      // `cleanup` truthy: the lines may still be there. Saying "nothing has been
      // charged" would be true of the charge and misleading about the cart, so it
      // gets its own error rather than the price-changed copy. That half is
      // unchanged, and it already reduces all five of the cleanup's refusals
      // correctly.
      //
      // `addUnsettled` is the new half, and it is the whole reason this unit
      // exists. A cleanup that came back CLEAN is evidence about the cart only if
      // the add's fate was already settled when it ran -- and for a request the
      // origin never answered it was not. The sequence that costs money is: client
      // gives up at the deadline, cleanup zeroes an empty cart and reports clean,
      // THEN the origin applies the add. Nothing the client can read distinguishes
      // that from a genuine miss, so it must not claim to.
      //
      // ⚠ AND IT COSTS THE CUSTOMER TIME, DELIBERATELY AND WITH THE BILL WRITTEN
      // DOWN. The cleanup runs on a submit that has ALREADY failed -- on a rejected
      // add, after the full fifteen-second deadline -- and adds a read and a write,
      // each allowed fifteen more, with the form frozen and the button still
      // reading "placing your order". The other write path in this file settled the
      // same question the other way, at CHECKOUT_WAIT_MS: holding a customer's
      // press for half a minute to protect a $0.00 line is a worse failure than the
      // one being fixed. What differs is what is being protected. There it was a
      // $0.00 line; here it is every chargeable line of the order, live in the cart
      // behind a message saying nothing was charged. So the wait is NOT capped, and
      // the reason is recorded here rather than left to be re-derived by whoever
      // next reads a slow submit as a defect. The bill is two calls rather than
      // three only because the cleanup's confirm now rides the update's own
      // response instead of taking a second cart read.
      //
      // ── AND THE HALF THAT IS NEW ON 11 Sep 2026: AN ATOMIC REFUSAL HAS NOTHING
      // OF OURS TO STRAND ──
      //
      // A failed add that came back with a Cart Error body is the origin saying it
      // evaluated the batch and applied none of it. So a cleanup that then fails
      // -- a 502 on the zeroing write, or a refusal because another tab took the
      // cart -- says nothing about THIS submit's lines: there are none. Before
      // this stack that customer got Shopify's own wording immediately; sending
      // them the strand warning instead would be a REGRESSION, telling them their
      // order may be sitting in the cart when the client has measured evidence
      // that it is not.
      //
      // THE FOREIGN-WRITER CASE IS THE ONE WORTH SAYING OUT LOUD. If the unwind
      // was refused because another tab took the cart, that cart may well hold
      // chargeable lines -- THEIRS. They belong to a submit that is still running
      // in another tab, under its own lock, and it will tell its own customer what
      // became of them. Warning ours about a cart they did not fill, over an order
      // the origin refused, is the wrong sentence to the wrong person.
      var addRefusedOutright = !!addFailure && !addUnsettled;
      if (addUnsettled || (cleanup && !addRefusedOutright)) {
        throw new Error('stranded: ' + problem +
                        ' [' + (cleanup || 'add_outcome_unknown') + ']');
      }
      // AND THE PREFIX IS THE CUSTOMER'S COPY. messageFor() passes `add_failed: `
      // through verbatim, which is right when the reason is Shopify's own
      // shopper-facing wording and wrong when it is a status line this file
      // synthesised: a customer reading `HTTP 503` has been told nothing. The
      // sibling prefix has no branch of its own, deliberately, so it lands on the
      // section's generic line -- and it is a distinct reason code, so an operator
      // reading the failure record can still tell the two apart.
      if (!addFailure) throw new Error('mismatch: ' + problem);
      throw new Error((addCopy ? 'add_failed: ' : 'add_failed_unexplained: ') + problem);
    }

    // A STALE ATTEMPT MUST NOT NAVIGATE. `attempt` is the token the submit
    // handler wrote on the form (see the counter above bind()); a bfcache
    // restore replaces it, so an attempt that was frozen and settles late finds
    // a token that is no longer its own -- and a customer who pressed Back to
    // change their order is not thrown at checkout for the configuration they
    // just abandoned. The failure record is left alone for the same reason: it
    // belongs to whatever attempt is live now.
    if (form.dataset.rfObAttempt !== attempt) return;
    // Only now. Clearing earlier would lose the record of the failures that
    // preceded an assert failure.
    clearFailures();
    window.location.assign(route('/checkout'));
  }

  /* ── binding ──────────────────────────────────────────────────────────── */

  function setBusy(form, busy) {
    var wrap = form.querySelector('[data-rf-ob-submit-wrap]');
    var btn = form.querySelector('[data-rf-ob-submit]');
    if (btn) {
      // aria-disabled, not `disabled`: a disabled button cannot hold focus, so a
      // keyboard user is dumped to the top of the document with nothing
      // announced. It does NOT prevent activation, which is why the guard below
      // is the real defence rather than a belt on top of one.
      btn.setAttribute('aria-disabled', busy ? 'true' : 'false');
      swapLabel(form, btn, busy);
    }
    if (wrap) wrap.setAttribute('aria-busy', busy ? 'true' : 'false');
    // AND on the form. The wrap is a <span> inside .rf-ob-go, in the summary
    // card in .rf-ob-rail -- it is not an ancestor of the payment and USB cards
    // in .rf-ob-col, so order-builder.liquid's `[aria-busy='true']
    // .rf-ob-choice` rule (D9) could never match through it. The form is the
    // one element that contains every control this function freezes, which is
    // exactly what `aria-busy` on it claims. The wrap keeps its own attribute:
    // it is what the submit button's own treatment reads.
    form.setAttribute('aria-busy', busy ? 'true' : 'false');
    // Freeze the controls: between submit and redirect the summary must not be
    // able to drift away from what is being charged.
    [].slice.call(form.querySelectorAll('input, [data-rf-ob-inc], [data-rf-ob-dec]'))
      .forEach(function (n) {
        if (busy) n.setAttribute('disabled', '');
        else n.removeAttribute('disabled');
      });
  }

  /**
   * The in-flight label (D9).
   *
   * A dimmed button carrying the same words it carried a moment ago is
   * indistinguishable from a page that has hung -- and the gap here is the WHOLE
   * busy window, from the swap to the redirect or to the error. That is up to
   * SEVEN sequential cart calls at a 15-second deadline each, so the ceiling the
   * code permits is 105 seconds.
   *
   * ⚠ SEVEN IS DERIVED, NOT MEASURED, AND IT SAID THREE UNTIL 11 Sep 2026 --
   * the same stale figure the lock comment carried, in a second place the sweep
   * that fixed the first one missed. Re-derive it here rather than copying it:
   * five cart calls on the run (clear, add, the verify read, the attribute
   * rewrite and its re-read) and two on the unwind (the zeroing update and its
   * confirming read, the second a fallback rather than a certainty), which is the
   * count the heartbeat test pins statically. Three was right when the phase was
   * clear -> add -> read, and every unit since has LENGTHENED this freeze: the
   * attribute-rewrite recovery, then an unwind that now runs on transport
   * failures it used to throw past. Nothing has observed a submit taking that
   * long -- the suite shadows AbortController, so nothing in CI arms the deadline
   * at all -- so read it as the ceiling, not as a duration anyone has seen.
   *
   * The `aria-disabled` above says "not now" to assistive technology; this is the
   * only thing that says it on screen.
   *
   * THE ORIGINAL IS STASHED ON THE BUTTON, not recomputed from `submit_label`.
   * Reading the setting back would need a second attribute and would go wrong
   * the moment a merchant edits the copy in the theme editor mid-flight; the
   * text that was on the button is by definition the text to put back.
   *
   * A BLANK ATTRIBUTE SKIPS THE SWAP ENTIRELY, which is the promise the
   * setting's own info line makes ("leave blank to keep the normal label"). The
   * attribute being ABSENT skips it too -- that is markup older than this asset,
   * where the previous behaviour is the only one known to be intended.
   */
  function swapLabel(form, btn, busy) {
    var busyText = form.getAttribute('data-rf-ob-busy-label');
    if (!busyText) return;
    if (busy) {
      // Guarded, because a second stash would record the busy label as the
      // original and leave it on the button forever. Both routes to a second
      // setBusy(true) with no unwind between them are now shut at the source:
      // the re-entrancy guard returns before a second submit, and a bfcache
      // restore unwinds first -- putting the label back and clearing this stash
      // -- then invalidates the attempt it froze, so that attempt's late
      // callbacks cannot reach setBusy() at all. This stays as the local
      // invariant it always was: one stash per busy period, whatever reaches it.
      if (btn.getAttribute('data-rf-ob-label-was') === null) {
        btn.setAttribute('data-rf-ob-label-was', btn.textContent);
      }
      btn.textContent = busyText;
    } else if (btn.getAttribute('data-rf-ob-label-was') !== null) {
      btn.textContent = btn.getAttribute('data-rf-ob-label-was');
      btn.removeAttribute('data-rf-ob-label-was');
    }
  }

  /**
   * Show the error, and TAKE THE CUSTOMER TO IT (D9).
   *
   * The error node lives inside the summary card, which on a phone is ~1,500px
   * below the inputs the customer has been using -- and the submit they just
   * pressed is a further card-height down it. Setting `textContent` and
   * unhiding, which is all this did until now, put a message on a part of the
   * page nobody was looking at: the customer saw a button come back to life
   * having apparently done nothing.
   *
   * THE ORDER OF THE THREE STEPS IS THE WHOLE FIX, and none of them is
   * interchangeable:
   *
   *   1. unhide FIRST -- a `display: none` element has no box, so
   *      scrollIntoView on it scrolls nowhere and reports nothing.
   *   2. scrollIntoView({ block: 'center' }) -- centred rather than
   *      `start`, because the summary card is taller than a phone viewport and
   *      `start` would put the message at the top with the button off-screen
   *      below it. It inherits the reduced-motion gate: `scroll-behavior:
   *      smooth` in rf-base.css sits inside a prefers-reduced-motion query.
   *   3. focus({ preventScroll: true }) LAST -- a bare focus() performs its own
   *      scroll, which would fight the animation step 2 just started. The node
   *      carries tabindex="-1" in order-builder.liquid; without it a <p> cannot
   *      take focus and this line is silently a no-op.
   *
   * role="alert" plus a focus move will typically announce twice on a screen
   * reader. Accepted (D9): the alternative is an error the customer is never
   * taken to.
   */
  function showError(form, message) {
    var box = form.querySelector('[data-rf-ob-error]');
    if (!box) return;
    box.textContent = message;
    box.removeAttribute('hidden');
    if (box.scrollIntoView) box.scrollIntoView({ block: 'center' });
    if (box.focus) box.focus({ preventScroll: true });
  }

  function hideError(form) {
    var box = form.querySelector('[data-rf-ob-error]');
    if (!box) return;
    box.textContent = '';
    box.setAttribute('hidden', '');
  }

  /**
   * A STRAND, ASKED IN TWO PLACES AND WRITTEN DOWN IN ONE.
   *
   * messageFor() dispatches on this prefix to choose the copy. The submit handler's
   * rejection path asks the same question for a different reason: a cart that may
   * still hold a chargeable order is the ONE failure a stale attempt must still put
   * on the page. Two copies of the prefix would be two rules that can drift apart,
   * and the second reader was added long after the first.
   *
   * THE PREFIX IS OURS, so this is not the thing the file forbids. Branching on a
   * human-readable message is forbidden because Shopify LOCALISES `description`;
   * `stranded: ` is written by this file, in this file, and never translated.
   */
  function isStranded(err) {
    return String(err && err.message ? err.message : err).indexOf('stranded: ') === 0;
  }

  function messageFor(form, err) {
    var m = String(err && err.message ? err.message : err);
    // Shopify's own wording for stock and variant problems is written for
    // shoppers, so it is passed through. Everything else gets the section's
    // merchant-editable line.
    var generic = form.getAttribute('data-rf-ob-error-generic') ||
                  'Something went wrong adding your order. Nothing has been charged — please try again.';
    // THE MINIMUM ORDER (R2). It refuses before any cart call is made, which is
    // why this reason needs copy of its own rather than one of the five below:
    // most of those are framed around "Nothing has been charged", the right
    // sentence for a submit that got part way and the wrong one for a
    // precondition that never started -- it answers a question the customer had
    // not thought to ask and does not tell them what to add.
    //
    // ⚠ THIS SAID "the only branch here that is NOT a submit failure" AND THAT
    // EVERY ONE OF THE FIVE IS FRAMED AROUND THAT SENTENCE. Both were already
    // false when they were written, and reading the five defaults side by side on
    // 11 Sep 2026 is what found it: the busy branch below ALSO refuses before any
    // cart call, and its copy does not mention charging at all. Corrected in the
    // section's `info` text too, where a merchant reads the same claim.
    //
    // ITS PLACEMENT IS FREE, unlike the mismatch pair below, and that is a
    // measured claim rather than a hopeful one. `nothing_to_order: ` is a prefix
    // of none of the other five reasons and none of them is a prefix of it, which
    // tests/order-builder.test.js's U3 test pins in BOTH directions -- because
    // indexOf(prefix) === 0 is not symmetric, so one direction proves nothing. So
    // this branch can neither shadow another nor be shadowed, and it is placed
    // first only because it is the first refusal in the sequence.
    if (m.indexOf('nothing_to_order: ') === 0) {
      return (form.getAttribute('data-rf-ob-error-minimum') ||
              'We cannot place this order as it stands. Check the album and ' +
              'loose-photo counts, add whatever is missing, and try again. If they ' +
              'already look right, reload the page or get in touch and we will place ' +
              'the order with you. A USB drive on its own is not an order — it ' +
              'carries the images from whatever you send us.');
    }
    // THE ALBUM MINIMUM (ALBUM-MIN U2). SAME SETTING AS THE BRANCH ABOVE, DIFFERENT
    // BUILT-IN, and the split is deliberate. Sharing `error_minimum` is what
    // keeps this from being an eighth merchant setting on a section already 55
    // over theme check's maximum. Not sharing the built-in is what keeps the
    // fallback TRUE: a customer refused here is holding photo sets, so "or one
    // set of loose photos" would be advice they have already taken.
    //
    // ⚠ THE MERCHANT-EDITABLE HALF IS SHARED AND THE BUILT-INS ARE NOT, WHICH IS
    // BACKWARDS FROM WHAT IT LOOKS LIKE. ALBUM-MIN U5 rewrote `error_minimum` to
    // be true of all THREE situations it now covers -- the empty order, this one,
    // and the never-booted submit that reaches `nothing_to_order: ` through
    // readState()'s zero defaults -- which cost it the ability to name what is
    // missing. The two built-ins below it can each name one, because each is
    // reached by exactly one token. So a merchant who blanks the setting gets
    // SHARPER copy than one who customises it, and a merchant who customises it
    // is writing one sentence for three states whether they know it or not. The
    // setting's `info` says so. Do not "fix" this by giving the two tokens two
    // settings without asking: no-new-settings was the decision, and an eighth
    // setting on a section already 55 over theme check's maximum is not free.
    if (m.indexOf('albums_required: ') === 0) {
      return (form.getAttribute('data-rf-ob-error-minimum') ||
              'Add at least one album before placing your order.');
    }
    // THE GUARANTEE LINE (R4), AND IT IS THE SEVENTH SETTING. buildCartPayload()
    // refuses the order outright when the Guarantee variant is missing, priced, or
    // unavailable -- the chosen failure mode over silently dropping the line -- so
    // this branch is the difference between an operator reading "the Guarantee
    // product setting is wrong" and a merchant watching the whole store answer
    // every submit with "Something went wrong". A refusal this broad has to say
    // what to fix.
    //
    // UNLIKE `nothing_to_order: ` ABOVE, THIS ONE IS A FAILURE. It rejects out of
    // run(), so it records a failure and console.errors, and both are right: a
    // customer stepping to zero albums has not failed at anything, and a store
    // that cannot resolve its own Guarantee variant has.
    //
    // ITS PLACEMENT IS FREE, on the same measured footing as the branch above:
    // `guarantee_unavailable: ` is a prefix of none of the other six reasons and
    // none of them is a prefix of it, pinned in BOTH directions by
    // tests/order-builder-cart.test.js -- indexOf(prefix) === 0 is not symmetric,
    // so one direction proves nothing. It sits here because it is the third
    // refusal in this file's header sequence, not because it has to.
    if (m.indexOf('guarantee_unavailable: ') === 0) {
      return (form.getAttribute('data-rf-ob-error-guarantee') ||
              'Your order includes the Photokive Guarantee, and we could not add ' +
              'it just now. Nothing has been charged — please get in touch and we ' +
              'will place the order with you.');
    }
    // ORDER MATTERS, AND IT IS NOT ALPHABETICAL. The specific prefix is tested
    // FIRST because the general one matches it too: 'mismatch: an automatic
    // discount...'.indexOf('mismatch: ') is 0. With the general test above this
    // one, this branch could never run -- the setting existed, was documented,
    // and was unreachable, so a store-wide automatic discount told the customer
    // "our prices have just changed, reload the page", which is false and
    // useless. Any new `mismatch: <reason>` branch goes above the general one too.
    //
    // Distinct reasons, distinct copy: plan decision D4 asked for the discount
    // case to be operator-legible specifically.
    if (m.indexOf('mismatch: an automatic discount') === 0) {
      return (form.getAttribute('data-rf-ob-error-discount') ||
              'A store-wide discount is running that this page cannot total ' +
              'correctly, so we stopped before charging anything. Please get in ' +
              'touch and we will place the order with you.');
    }
    if (m.indexOf('mismatch: ') === 0) {
      return (form.getAttribute('data-rf-ob-error-mismatch') ||
              'Our prices have just changed. Nothing has been charged — reload the page to see the current total.');
    }
    if (m.indexOf('busy_elsewhere') === 0) {
      return (form.getAttribute('data-rf-ob-error-busy') ||
              'Another tab is already placing this order. Finish there, or reload ' +
              'this page to start again.');
    }
    if (isStranded(m)) {
      return (form.getAttribute('data-rf-ob-error-stranded') ||
              'We could not confirm your order, and your cart may still hold it. ' +
              'Nothing has been charged. Please review your cart before paying, or ' +
              'contact us and we will sort it out.');
    }
    // THE ONLY BRANCH THAT PASSES A MESSAGE THROUGH, and it is reached only when
    // run() found Shopify's own shopper-facing wording on the failing response.
    // Where it found none, run() throws the sibling reason instead -- which has NO
    // branch here on purpose, so it falls to the generic line below rather than
    // showing a customer a status code. Do not give it one.
    if (m.indexOf('add_failed: ') === 0) return m.slice('add_failed: '.length);
    return generic;
  }

  /* ── the cross-tab lock ───────────────────────────────────────────────────
     The re-entrancy guard below lives on one document's form node, but the cart it
     protects is scoped to the SESSION -- shared by every tab, because it is a
     cookie. clear.js is global and add.js is additive, so two tabs submitting
     within a few seconds of each other interleave into a cart neither one
     asserted. Worse, above 20 albums both tabs resolve the SAME variant (the rate
     tier caps at 20), so the loser's cleanup zeroes the winner's line while the
     winner is sitting on Shopify's checkout unable to pay.

     Not hypothetical: the pricing calculator now emits ?albums=N links, which
     people open in new tabs.

     localStorage, not sessionStorage -- sessionStorage is per-tab and would
     reproduce the bug it is meant to fix. Every access is wrapped: Safari private
     mode throws on write, and a submit must never fail because a diagnostic
     could not be stored. */
  var LOCK_KEY = 'rf-ob-submit-lock';
  var LOCK_TTL_MS = 30000;

  /* ── AND A SECOND LOCK, FOR THE /cart RE-ADD ONLY (R6) ─────────────────────
     Its own key, deliberately: the re-add must never TAKE the submit lock, or a
     page load in one tab would answer a submit in another with "Another tab is
     already placing this order", which would be false. It still needs a
     cross-document guard of its own, because `reAddPending` is per document and
     two /cart tabs opened together both read a Guarantee-less cart and both POST
     -- $0.00 x 2 on the last screen before payment, self-healing only on a later
     load.

     A FEW SECONDS, NOT THIRTY. It brackets one read and at most two writes on a
     page the customer is looking at, and a crashed tab must not stop the next
     load from self-healing. Slower than this and the second tab proceeds, which
     is the pre-existing behaviour and bounded by the quantity correction. */
  var READD_LOCK_KEY = 'rf-cart-readd-lock';
  var READD_LOCK_TTL_MS = 5000;

  /**
   * The owner token this DOCUMENT last wrote to each lock, by key.
   *
   * Never cleared on release, and that is the point: after a `pageshow` release
   * and another tab's takeover, this still remembers what we wrote, so
   * lockTakenOver() can tell "someone else holds it" from "nobody does".
   */
  var lockTokens = {};

  /* THE THREE PRIMITIVES ARE PARAMETERISED BY KEY, and the parameter is optional
     because every call site that predates the second lock means the submit one.
     tests/listener-placement.test.js pins `releaseLock()` -- with no argument --
     inside done() and once after the pageshow loop, so the submit path is left
     writing all three bare rather than carrying two conventions. */

  function lockHeldElsewhere(key, ttl) {
    try {
      var raw = window.localStorage.getItem(key || LOCK_KEY);
      if (!raw) return false;
      var at = Number(JSON.parse(raw).at);
      if (!at) return false;
      // A stale lock must expire, or one crashed tab wedges the store for the
      // customer permanently.
      return (Date.now() - at) < (ttl || LOCK_TTL_MS);
    } catch (e) {
      return false;
    }
  }

  /**
   * CLAIM THE LOCK, AND RECORD THE CLAIM ONLY WHERE THE WRITE LANDED.
   *
   * THE ORDER OF THESE TWO STATEMENTS IS THE WHOLE FUNCTION, and it used to be
   * the other way round. `lockTokens[k]` was assigned ABOVE the try, so a
   * document whose setItem threw believed it owned a lock it had never stored --
   * and on a storage AT QUOTA, where reads still answer and only writes fail,
   * that belief is not harmless. The claim-then-verify read-back in bind() then
   * read whatever record was already in that key, found an owner that was not
   * ours, and refused the submit as busy. Nothing in this document could ever
   * clear that record again: releaseLock() writes through the same failing
   * storage, takeLock() cannot overwrite it, and a reload resets this map and not
   * the record. EVERY subsequent submit repeated the refusal, on a browser that
   * sold normally before the read-back existed.
   *
   * RECORDING NOTHING IS THIS FILE'S STANDING RULE APPLIED TO THE NEW CHECK. A
   * lock we cannot store is a lock we do not hold, so touchLock(),
   * lockTakenOver() and releaseLock() each fall to their documented safe default
   * for this document -- no heartbeat, no takeover claimed, nothing removed --
   * and the submit proceeds. That is the same trade removeOurLines() records for
   * Safari in private mode: a cleanup refused on every submit is worse than a
   * cleanup that ran without a cross-document guard.
   */
  function takeLock(key) {
    var k = key || LOCK_KEY;
    var owner = token();
    try {
      window.localStorage.setItem(k, JSON.stringify({ at: Date.now(), owner: owner }));
      lockTokens[k] = owner;
    } catch (e) { /* a lock we cannot store is a lock we do not hold */ }
  }

  /**
   * RE-STAMP A LOCK WE STILL OWN, AND ONLY ONE WE STILL OWN.
   *
   * ── WHY A HEARTBEAT RATHER THAN A BIGGER LOCK_TTL_MS ──────────────────────
   *
   * The TTL has to answer two questions that pull in opposite directions: "is
   * the tab holding this still alive?" and "has a crashed tab wedged the store?"
   * A constant large enough for the first is a constant that answers the second
   * with "for that long, yes". Thirty seconds is chosen for the CRASH case --
   * how long a customer whose other tab died must wait before they can order --
   * and this function is what stops a LIVE writer from being mistaken for a dead
   * one, by moving the stamp forward before every cart call it makes. A
   * threshold defect is not fixed with a new threshold
   * (docs/solutions/best-practices/a-pass-fail-rule-hides-the-approach-to-its-own-boundary-2026-09-04.md).
   *
   * ── ONLY OUR OWN, AND THIS IS THE HALF THAT IS EASY TO GET WRONG ──────────
   *
   * A heartbeat that re-stamped whatever it found would keep ANOTHER tab's
   * record warm -- including a crashed one -- and the store would be wedged by
   * exactly the mechanism the TTL exists to prevent, now with a writer holding
   * it open. So the owner is compared first, and a record naming someone else is
   * left untouched.
   *
   * `owner` IS CARRIED THROUGH RATHER THAN RE-DERIVED, so lockTakenOver() keeps
   * working across a heartbeat. Writing `{at: Date.now()}` alone would keep the
   * lock alive and switch the unwind's only guard off in the same statement: an
   * ownerless record makes lockTakenOver() answer false for every tab, and the
   * loser of a race would then zero the winner's cart believing it owned it.
   *
   * EVERY ACCESS WRAPPED, and a failure is silent. A heartbeat is a diagnostic;
   * "a lock we cannot store is a lock we do not hold" is this file's standing
   * stance, and a submit must never fail because one could not be written.
   */
  function touchLock(key) {
    var k = key || LOCK_KEY;
    var mine = lockTokens[k];
    if (!mine) return;
    try {
      var raw = window.localStorage.getItem(k);
      if (!raw) return;
      var rec = JSON.parse(raw);
      if (String(rec.owner || '') !== mine) return;
      window.localStorage.setItem(k, JSON.stringify({ at: Date.now(), owner: mine }));
    } catch (e) { /* a heartbeat we cannot store is a heartbeat that did not happen */ }
  }

  /**
   * RELEASE A LOCK WE STILL OWN, AND ONLY ONE WE STILL OWN.
   *
   * THE SAME COMPARISON touchLock() MAKES, FOR A STRICTER REASON. A heartbeat
   * that re-stamped a foreign record keeps another tab's lock warm; a release
   * that removes one HANDS THAT TAB'S CART AWAY WHILE IT IS STILL WRITING. This
   * was a bare removeItem with no ownership test at all, three lines under the
   * long comment explaining why the heartbeat needs one.
   *
   * BOTH CALLERS FIRE IN DOCUMENTS THAT NEVER TOOK THE LOCK. The bfcache handler
   * at the foot of this file releases UNCONDITIONALLY on every restore, and
   * done() runs on a submit that may have been refused. So: tab A takes the lock
   * and starts its write phase; tab B is restored from bfcache and deletes A's
   * record. A's touchLock() then finds no record and returns without re-taking
   * it, so the lock stays gone for the rest of A's write phase; B is admitted
   * mid-write, calls the clear endpoint, and destroys the cart A has already
   * asserted. And A's unwind is disarmed in the same move, because
   * lockTakenOver() answers false on an ABSENT record -- so A believes it still
   * owns the cart, zeroes B's lines, and clears the version key that would have
   * let the /cart re-add repair anything.
   *
   * EVERY ACCESS WRAPPED AND A FAILURE SILENT, exactly as above. Storage we
   * cannot read is storage whose record we cannot claim, and a submit must never
   * fail because a release could not be made.
   */
  function releaseLock(key) {
    var k = key || LOCK_KEY;
    var mine = lockTokens[k];
    if (!mine) return;
    try {
      var raw = window.localStorage.getItem(k);
      if (!raw) return;
      if (String(JSON.parse(raw).owner || '') !== mine) return;
      window.localStorage.removeItem(k);
    } catch (e) { /* a record we cannot read is a record we must not remove */ }
  }

  /**
   * Whether the lock is now held by SOMEONE ELSE -- a different owner token from
   * the one this document last wrote. removeOurLines() is the only caller; its
   * header says why this replaced a quantity comparison.
   *
   * NOT TTL-GATED, on purpose. SEVEN sequential cart calls at a 15-second
   * deadline each outlast LOCK_TTL_MS, so a lock that has gone stale by the clock
   * can still belong to a tab that is mid-write. What is being asked here is
   * "did another writer take this cart", and the answer to that does not expire.
   *
   * ⚠ SEVEN IS DERIVED, NOT MEASURED, and it said THREE until 11 Sep 2026. The
   * write phase is clear -> add -> the verify read -> the attribute rewrite ->
   * its re-read -> the unwind's update -> the unwind's confirm. Three was right
   * when the phase was its first three calls and was never re-derived as the
   * other four were added. The figure is the COUNT of sequential cart calls on
   * the longest path multiplied by the deadline the transport ALLOWS -- 105s
   * against a 30s TTL. The SEVENTH is now a fallback rather than a certainty:
   * the unwind confirms from the update response and only re-reads the cart when
   * that body is unusable, so seven is the ceiling and six the usual figure. Nothing has observed a submit taking that long; the suite
   * shadows AbortController, so nothing in CI arms the deadline at all. Read it
   * as the ceiling the code permits, not as a duration anyone has seen, and
   * re-derive it rather than copying it forward.
   *
   * touchLock() now moves the stamp forward before each of those calls, so a
   * live writer no longer looks expired to the admission check. That does not
   * make a TTL gate here safe: a heartbeat that could not be stored is a
   * heartbeat that did not happen, and the question this function asks has a
   * different answer from the one the TTL answers.
   *
   * FALSE ON EVERY UNCERTAINTY: no record, no owner on the record, unreadable
   * storage, or a lock we never took. Each of those is today's behaviour, and the
   * caller explains why proceeding is the safer default there.
   */
  function lockTakenOver(key) {
    var k = key || LOCK_KEY;
    var mine = lockTokens[k];
    if (!mine) return false;
    try {
      var raw = window.localStorage.getItem(k);
      if (!raw) return false;
      var owner = String(JSON.parse(raw).owner || '');
      return !!owner && owner !== mine;
    } catch (e) {
      return false;
    }
  }

  /* ── one identity per submit attempt ──────────────────────────────────────
     Every deferred callback in bind() mutates state shared by the whole page --
     the in-flight flag, the busy DOM, the cross-tab lock -- and a bfcache
     restore can hand the customer a live form while an earlier attempt's
     promise is still pending, because the pageshow handler at the foot of this
     file clears the flag that would otherwise refuse a second submit. Bumping
     this counter there invalidates whatever attempt was frozen, so a first
     attempt that settles late cannot unwind the SECOND attempt's busy state,
     release the lock the second attempt is holding, or navigate a customer who
     has since started a new configuration.

     Chromium and WebKit both refuse to bfcache a document with an in-flight
     fetch, so none of that is reachable today. This guards the invariant, it
     does not fix an observed bug.

     Module level, like the pageshow registration and for the same reason:
     `window` outlives every form, and the counter has to be the same one for
     all of them. */
  var attemptSeq = 0;

  function bind(form) {
    if (form.dataset.rfCartBound === 'true') return;
    form.dataset.rfCartBound = 'true';

    // The bfcache restore is handled ONCE at module level, at the foot of this
    // file -- `window` outlives every form, so registering it here stacked one
    // listener per section render (U5).

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      // SYNCHRONOUS re-entrancy guard, set before anything can await. This is
      // what actually stops a double submit: add.js is ADDITIVE, so a second
      // pass would double every quantity.
      if (form.dataset.rfObInflight === 'true') return;
      form.dataset.rfObInflight = 'true';

      // THIS attempt's identity, captured in the closure and written to the DOM.
      // Every callback below reads the attribute back and compares it to the
      // local before touching anything shared. On the form rather than only in
      // the closure because the invalidating side -- pageshow -- has no
      // reference to the attempt it is invalidating.
      var myAttempt = String(++attemptSeq);
      form.dataset.rfObAttempt = myAttempt;

      hideError(form);
      setBusy(form, true);

      // ONE REFUSAL, TWO REASONS TO REACH IT -- the lock was already held, or the
      // claim below was not confirmed. Factored because the two must unwind
      // IDENTICALLY: an exit that forgot the in-flight flag would leave a form
      // that can never be submitted again without a reload. It deliberately does
      // NOT call releaseLock(): on both paths the record belongs to another tab,
      // and clearing it would hand the cart to whoever submits next.
      var refuseBusy = function () {
        showError(form, messageFor(form, new Error('busy_elsewhere')));
        form.dataset.rfObInflight = 'false';
        setBusy(form, false);
      };

      // Another tab owns the cart. Distinguishable from a price change, and worth
      // its own message: telling this customer the prices changed would be false.
      if (lockHeldElsewhere()) {
        refuseBusy();
        return;
      }
      takeLock();
      // CLAIM, THEN VERIFY -- and the verify is the half that makes the claim
      // mean anything. The admission check and the write above are two separate
      // storage operations with no compare-and-swap between them, so two tabs
      // submitting in the same window both read "free" and both write. The LATER
      // write owns the record. Without this read-back the EARLIER tab proceeds
      // believing it holds the lock, and lockTakenOver() then answers false for
      // it -- so the unwind, whose only guard that is, would zero lines out of a
      // cart this document does not own.
      //
      // AN UNREADABLE OR ABSENT RECORD STILL MEANS PROCEED, because that is what
      // lockTakenOver() returns there and it is the same trade removeOurLines()
      // documents: on Safari in private mode every write throws, and refusing
      // every submit on that browser is the worse failure.
      if (lockTakenOver()) {
        refuseBusy();
        return;
      }

      var map = readMap(form);
      // PREFER THE BUILDER'S COMMITTED STATE. rf-order-builder.js keeps a
      // `committed` object because the field is deliberately allowed to hold
      // uncommitted text, so reading input.value here could build a payload for a
      // count the summary never displayed -- which is exactly what R3 forbids. The
      // old comment claimed "the DOM is the state ... so a second reader cannot
      // disagree with the first", and that is true of this theme generally and
      // false of these two fields specifically. The DOM read stays as the
      // fallback, so a builder that failed to boot still refuses safely rather
      // than throwing here.
      var state = (typeof form.rfObState === 'function' && form.rfObState()) ||
                  readState(form);

      var done = function () {
        // NOT OURS, NOT OURS TO UNWIND. A first attempt settling after a
        // bfcache restore let the customer resubmit would otherwise clear the
        // second attempt's busy state and release the lock the second attempt
        // is holding -- two unwinds for one submit, and a live-looking form
        // over a cart call still in flight. `pageshow` releases the lock once
        // itself; a stale done() must not release it a second time.
        if (form.dataset.rfObAttempt !== myAttempt) return;
        form.dataset.rfObInflight = 'false';
        setBusy(form, false);
        releaseLock();
      };

      if (!map) {
        recordFailure('no_variant_map');
        showError(form, messageFor(form, new Error('no_map')));
        done();
        return;
      }
      // THE MERCHANT'S LIMITS, NOT THE SHIPPED ONES -- the same
      // pricing.selfServeLimits(map) call rf-order-builder.js's render() and
      // rf-order-payload.js's first guard make. `map` is non-null by here (the
      // `!map` refusal is immediately above), but the helper carries the guard
      // anyway so every site spells the read once and identically.
      if (pricing.exceedsSelfServe(state, pricing.selfServeLimits(map))) {
        // The UI should already have swapped to the contact route, so reaching
        // here means the two disagreed. Refuse rather than sell.
        showError(form, messageFor(form, new Error('over_ceiling')));
        done();
        return;
      }
      // 9b. THE OTHER PRECONDITIONS -- PLURAL SINCE ALBUM-MIN U2 (R2). Two of
      // them are refused here. An order with no albums and no loose photos
      // (`nothing_to_order: `, in EITHER switch position, and the state a
      // never-booted builder lands on through readState()'s zero defaults); and,
      // while `allow_zero_album_orders` is off, an order holding photo sets but
      // no albums (`albums_required: `). minimumRefusal() below names both
      // prefixes literally. The check above is the precedent, comment included,
      // and these sit beside it for the same reason -- a precondition is refused
      // BEFORE anything is attempted, not part way through.
      //
      // WHAT THIS BUYS OVER THE THROW INSIDE run(), stated precisely because the
      // easy version of it is wrong: buildCartPayload() is called at the TOP of
      // run(), synchronously, above the first await -- so today the throw already
      // rejects before /cart/clear.js is reached, and the customer's existing cart
      // is not emptied either way. Verified by reading run(), and pinned by the
      // control in tests/order-builder-cart.test.js, which drives a real submit
      // and asserts on the calls fetch actually received.
      //
      // Three things it buys, none of them that one:
      //   1. NO FAILURE RECORD. run()'s rejection handler calls recordFailure(),
      //      which persists the reason for the session and rides onto the NEXT
      //      order as a `_prior_submit_failures` cart attribute. (This said
      //      `_prior_failures` until ALBUM-MIN U5. No attribute of that name has
      //      ever been written -- rf-order-payload.js spells it
      //      `_prior_submit_failures` at both the cap and the write -- so anyone
      //      grepping the shipped name from here found nothing.) Neither refusal
      //      is a failure: in the permissive position the customer stepped albums
      //      to 0 on the way to typing 3, and in the enforcing one they are on a
      //      page whose album floor did not hold. The ceiling check records
      //      nothing either, and this matches it.
      //   2. NO console.error. Same reason -- an operator reading the console for
      //      real cart failures should not be reading this one.
      //   3. THE GUARANTEE STOPS BEING A LINE ORDERING. "Above the first await"
      //      is one edit away from false, and the edit that breaks it would look
      //      like tidying. Here the refusal is structural: it returns before
      //      run() exists to be reordered.
      var unsellable = minimumRefusal(state, map);
      if (unsellable) {
        showError(form, messageFor(form, unsellable));
        done();
        return;
      }

      run(form, state, map, myAttempt).then(
        function () {
          // Navigation is under way. Deliberately NOT re-enabling: the page is
          // leaving, and re-enabling would invite a second submit into the gap.
          // The one resolve that arrives with the page NOT leaving is a stale
          // attempt whose redirect run() refused; doing nothing is right there
          // too, because the state now belongs to whatever attempt is live.
        },
        function (err) {
          // The log is unconditional: a rejection nobody is waiting for is
          // still worth seeing, and it writes nothing. Everything after it
          // records a failure against the session or moves focus on the page,
          // so it waits on the same identity check done() makes -- a stale
          // rejection must not scroll a customer to an error about the
          // configuration they have already replaced.
          console.error(err);
          // AND SO IS A STRAND -- THE ONE EXCEPTION TO THE GUARD BELOW.
          //
          // The guard is right about the other three things it protects: the
          // redirect, the failure record and the busy state all belong to whatever
          // attempt is live now. It was wrong about a cart that may still hold a
          // chargeable order, because that cart belongs to the SESSION rather than
          // to an attempt -- it is the same cart either way, and a customer who is
          // never told about it has no reason to go and look.
          //
          // COMPUTED ABOVE THE GUARD, SHOWN ONLY FROM INSIDE THE STALE BRANCH, so
          // the live path below still makes exactly the calls it always made and
          // nothing is announced twice.
          var strandCopy = isStranded(err) ? messageFor(form, err) : null;
          if (strandCopy && form.dataset.rfObAttempt !== myAttempt) showError(form, strandCopy);
          if (form.dataset.rfObAttempt !== myAttempt) return;
          recordFailure(reasonCode(err));
          showError(form, messageFor(form, err));
          done();
        }
      );
    });
  }

  /** A short, stable token for the failure record. Never the raw message. */
  function reasonCode(err) {
    var m = String(err && err.message ? err.message : err);
    var head = m.split(':')[0].trim();
    return head.replace(/[^A-Za-z0-9_]/g, '_').slice(0, 40) || 'unknown';
  }

  function setup(root) {
    var scope = root || document;
    var list = [].slice.call(scope.querySelectorAll('[data-rf-ob-form]'));
    if (scope !== document && scope.matches && scope.matches('[data-rf-ob-form]')) {
      list.push(scope);
    }
    list.forEach(bind);
  }

  function boot() {
    try {
      if (!pricing || !payloadMod) {
        throw new Error('rf-order-cart.js loaded before its dependencies');
      }
      setup(document);
      // R6, and a no-op everywhere but /cart: readCartGuarantee() returns null
      // unless the page rendered the config, and reAddGuaranteeLine() returns on
      // a null config before it reads anything. Deliberately not on
      // `shopify:section:load` -- it is a cart read plus at most one write, and
      // re-running it on every theme-editor re-render would buy nothing.
      //
      // NOT AWAITED HERE, BUT NO LONGER UNREACHABLE. boot() has nothing to do
      // with the result, so it does not wait; the promise is parked in
      // `reAddPending` instead, and the delegated `submit` listener at the foot
      // of this file is what waits on it -- for up to CHECKOUT_WAIT_MS -- if the
      // customer presses Checkout while it is still in flight.
      reAddGuaranteeLine(readCartGuarantee(document));
    } catch (e) {
      console.error(e);
    }
  }

  if (isCommonJS) {
    // Export only the pure functions. Nothing here touches the DOM, so the boot
    // path is skipped outright rather than stubbed.
    //
    // "PURE" IS A CLAIM ABOUT THE ARGUMENTS, NOT ABOUT THE DOM, and readState()
    // is the case that makes the distinction worth writing down. It queries a
    // form node -- so it is not DOM-free -- but the node is an argument, it is
    // read and never written, and nothing else in the function reaches out of it.
    // A four-property stub satisfies it, which is exactly what lets a test pin
    // the one thing about it that costs money: what an ABSENT album field
    // defaults to. The stub cannot make querySelector answer wrongly, so the
    // narrowness is real rather than convenient.
    //
    // minimumRefusal() is pure in the same sense: plain data in, an Error or null
    // out, and the payload it builds is discarded. It is exported because it is
    // step 9b -- the single reason a submit can be refused before the cart is
    // touched -- and a guard reachable only through a browser is a guard nothing
    // in CI can prove is armed.
    //
    // guaranteeLineAction() is exported on exactly the same footing as
    // assertCart(): it is the one decision on the /cart path, it can add a line
    // to a cart nobody is watching, and a decision reachable only through a
    // browser is a decision nothing in CI can prove is gated.
    module.exports = {
      assertCart: assertCart,
      cartError: cartError,
      allowedIds: allowedIds,
      messageFor: messageFor,
      readState: readState,
      minimumRefusal: minimumRefusal,
      guaranteeLineAction: guaranteeLineAction
    };
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    try {
      setup(e.target);
    } catch (err) {
      console.error(err);
    }
  });

  /* ── back from checkout ───────────────────────────────────────────────────
     A bfcache restore returns the DOM exactly as it left -- every input still
     `disabled`, the submit still aria-disabled and rfObInflight still 'true',
     because the success path deliberately never unwinds them (see run()'s
     resolve handler). Without this the customer pressing Back to change their
     order finds every control dead, a submit button that looks live and
     silently does nothing, and no hint that a reload is the only way out.
     Changing your mind at checkout is the most ordinary thing a configurator
     has to survive.

     ONE listener, registered once. `window` outlives every form, so the
     per-form version this replaces left a listener behind on every
     `shopify:section:load` -- each holding a detached form and skipping its own
     work, which made the accumulation invisible rather than harmless.

     It sits AFTER the CommonJS return above, deliberately: under `node --test`
     there is no `window` and a top-of-module registration takes the suite down.
     tests/listener-placement.test.js asserts that ordering, not just the
     placement. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;

    /* ── /cart FIRST, AND THAT ORDERING IS THE POINT (R6, U11) ───────────────
       `DOMContentLoaded` does not fire on a bfcache restore, so boot() does not
       run and the re-add would never be re-evaluated on the single most likely
       way to arrive back at /cart: pressing Back from Shopify's checkout. A
       customer who removed the Guarantee, reached checkout, then came back would
       find it still gone.

       IT SITS ABOVE THE EARLY RETURN ON AN EMPTY `forms` LIST, BECAUSE OF WHAT
       THAT RETURN MEANS HERE. It is about the builder -- a page with no bound
       form has no busy state, no in-flight flag and no attempt to unwind -- and
       /cart is exactly such a page. Below it, this call would be dead code on the
       only page it exists for. The unwind below is untouched: it still runs on
       the builder, in the same order, and the re-add is a no-op there because the
       builder page renders no /cart config.

       (The return is deliberately not quoted verbatim in this comment: the static
       pin in tests/order-builder-cart.test.js compares the two by index, and a
       copy of the line in prose above the call would satisfy it for the wrong
       reason -- the exact way a static reader lies.)

       IT IS NOT AWAITED HERE, and it never takes the SUBMIT lock -- it observes
       that one and takes a short-TTL key of its own. It reads the cart, and it
       stands down if a submit is in flight in any tab. The promise it returns is
       held in `reAddPending` for the Checkout button's sake, not for this
       handler's. */
    reAddGuaranteeLine(readCartGuarantee(document));

    // The live, bound forms. A form that never bound has nothing to unwind, and
    // a detached one cannot be reached by a document query.
    var forms = [].slice.call(
      document.querySelectorAll('[data-rf-ob-form][data-rf-cart-bound="true"]'));
    if (!forms.length) return;
    // INVALIDATE FIRST, RESET SECOND. The reset below is what lets the customer
    // submit again, so the attempt that was frozen has to be cut loose before
    // it happens -- not after, when a promise settling in the same task could
    // still match its own token. Bumping the counter and writing the new value
    // to every bound form is the whole mechanism: the frozen attempt's captured
    // local no longer matches the attribute, so its done(), its error path and
    // its redirect all return without touching the attempt started next.
    attemptSeq++;
    var restored = String(attemptSeq);
    forms.forEach(function (form) {
      form.dataset.rfObAttempt = restored;
      form.dataset.rfObInflight = 'false';
      setBusy(form, false);
      hideError(form);
    });
    // ONCE, not per form: the lock is a single localStorage key shared by every
    // tab and every builder on the page. Releasing it in the loop would be
    // correct-by-accident with one form and wrong the moment a second exists,
    // because the second release would clear a lock the first had just retaken.
    releaseLock();
  });

  /* ── the Checkout click, and the write it must not outrun (R6) ─────────────
     /cart's checkout control is a plain `<form action="{{ routes.cart_url }}"
     method="post">` holding one `name="checkout"` submit button
     (sections/cart.liquid), live from first paint and owned by no script. The
     `routes.` form of the action is why the anchor below is the wrapper and the
     button name rather than the action string: a locale prefix would move that
     string and this listener must not go quiet when it does. The re-add is two
     sequential round trips started un-awaited from
     boot(). A customer who clicks inside that window navigates to Shopify's
     checkout and the order is placed with no Guarantee line -- silently, with
     nothing logged, and unrecoverable once it is an order.

     ONE DELEGATED LISTENER, on `document`, because the form is inside a section
     that `shopify:section:load` can replace: a listener bound to the form node
     would be lost on the first theme-editor re-render, and re-binding it per
     render is the accumulation U5 removed. Delegation has neither problem.

     IT DOES NOTHING UNLESS A WRITE IS PENDING. No pending write, no
     preventDefault, no wait, no behaviour change at all -- which is every load
     but the one where the customer is faster than two fetches.

     THE 1200 ms CAP IS THE POINT, and it is not the fetch deadline. postJSON()
     allows FETCH_TIMEOUT_MS -- 15 seconds -- per call, twice over, and holding a
     customer's Checkout press for half a minute to protect a $0.00 line is a
     worse failure than the one being fixed. Past the cap the submit goes through
     and the re-add lands or does not; R6 self-heals on the next /cart visit,
     and an order placed without the line is recoverable by hand.

     PREVENTED ONCE, then re-submitted through the SUBMITTER. `form.submit()`
     would not do: it omits the submit button's name/value, and `checkout` in the
     POST body is what routes /cart to the checkout rather than to a cart
     re-render. requestSubmit() keeps the submitter and fires `submit` again --
     which this listener has to let through, hence `released`. */
  var released = null;

  document.addEventListener('submit', function (e) {
    var form = e.target;
    // Our own re-submit, coming back around. Let it go.
    if (form === released) return;
    // Nothing pending: the common case, and it costs one comparison.
    if (!reAddPending) return;
    if (!form || !form.closest || !form.querySelector) return;
    // THE /cart CHECKOUT FORM SPECIFICALLY, anchored on the very node
    // readCartGuarantee() reads its config from, so the listener and the write
    // it waits for can never disagree about which page they are on. The button
    // name is the second half: `.pk-cart` may hold other forms one day, and only
    // this one leaves the page for payment.
    if (!form.closest('[data-rf-cart-guarantee]')) return;
    if (!form.querySelector('[name="checkout"]')) return;

    var pending = reAddPending;
    var submitter = e.submitter || form.querySelector('[type="submit"]');
    e.preventDefault();

    var go = function () {
      released = form;
      try {
        if (form.requestSubmit) form.requestSubmit(submitter || undefined);
        else if (submitter && submitter.click) submitter.click();
        else form.submit();
      } finally {
        // Both re-submit routes dispatch synchronously, so the listener above
        // has already seen and passed this form by the time this runs. The
        // third route dispatches nothing, which is why the reset is here rather
        // than in the early return.
        released = null;
      }
    };
    Promise.race([pending, wait(CHECKOUT_WAIT_MS)]).then(go, go);
  });
})();
